import { auth, db, storage } from './firebase';
import { ensureCrmStorageAccessBeforeUpload } from './crmStorageAccess';
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  Timestamp,
  orderBy,
  limit,
  setDoc,
  updateDoc,
  deleteField,
  runTransaction,
  writeBatch,
  type DocumentData,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { enrichTimberCsvText } from './proposalCSVParser';
import { replaceStreamTitlePrefix, structureNamePrefixLabels } from './projectFileStreamGroups';
import { structureTrackKindLabel } from './projectFileStructureTracks';
import { filesInSameStructureRelease } from './projectFileGroupAssignment';
import { structureSelectionLabel, structureSetKey } from './structureSelectionLabel';
import { projectFileUploadBlockReason } from './projectFileUploadGuard';
import { csvListStreamKind, filenameLooksLikeHardwareList, isCadworkSteelCsv } from './proposalHardwareCsvParser';
import { deleteLeadDocument, findLeadDocumentByUrl } from './leadDocumentService';
import { leadCommentsCollectionRef } from './leadCommentsFirestore';
import { deleteHubFileReleasesForSourceFile } from './rfqHubFileReleaseService';
import { followPortalReleaseScope } from './portalFileReleaseService';
import type {
  PdfAnnotation,
  ProjectFile,
  ProjectFileKindTag,
  ProjectFileMoveRecord,
  ProjectFileStructureSetupRecord,
  ProjectQuoteType,
  ProjectFileSection,
  ProjectFileStream,
  ProjectFileStreamKind,
  ProjectQuoteStatus,
} from '../types';
import { pdfAnnotationMatchesProjectFile } from './projectFileCommentCounts';
import { displayNameFromFileRef, normalizeStoragePath } from './projectFileRef';
import { isThreeDRawFile, THREE_D_RAW_ACCEPT } from './projectFileThreeD';
import {
  parseProjectFileDetailStage,
  type ProjectFileDetailStage,
} from './projectFileVersionGroups';
import { claimAfterDroppingStructures } from './cncModelStructures';
import {
  FINANCIALS_FOLDER_PATH,
  isFinancialsFolderPath,
  isWritableProjectFolderPath,
} from './projectFileFolders';
import { mergeStreamVersions } from './projectFileStreamVersions';

export { isThreeDRawFile, THREE_D_RAW_ACCEPT, THREE_D_RAW_NAME_RE } from './projectFileThreeD';

export {
  countCommentsOnProjectFile,
  countUnreadCommentsOnProjectFile,
  pdfAnnotationMatchesProjectFile,
} from './projectFileCommentCounts';

const COLLECTION = 'projectFiles';
const FINANCIAL_COLLECTION = 'projectFinancialFiles';
const STREAMS_COLLECTION = 'projectFileStreams';
const STORAGE_PREFIX = 'projectFiles';
const FINANCIAL_STORAGE_PREFIX = 'projectFinancials';

function coerceStreamKind(raw: unknown): ProjectFileStreamKind {
  const k = typeof raw === 'string' ? raw : '';
  if (
    k === 'three_d_html' ||
    k === 'three_d_raw' ||
    k === 'two_d_pdf' ||
    k === 'timber_list_csv' ||
    k === 'hardware_list_csv' ||
    k === 'general'
  )
    return k;
  return 'general';
}

function streamFromFirestore(snap: { id: string; data: () => Record<string, unknown> }): ProjectFileStream {
  const d = snap.data();
  return {
    id: snap.id,
    projectId: d.projectId as string,
    title: (d.title as string) || 'Untitled track',
    streamKind: coerceStreamKind(d.streamKind),
    description: (d.description as string) || undefined,
    createdAt: (d.createdAt as { toDate: () => Date })?.toDate?.() ?? new Date(),
    createdBy: (d.createdBy as string) || '',
    updatedAt: (d.updatedAt as { toDate: () => Date })?.toDate?.() ?? new Date(),
    lastVersionIndex: typeof d.lastVersionIndex === 'number' ? d.lastVersionIndex : 0,
    headFileId: (d.headFileId as string) || undefined,
    lastSubmittedBy: (d.lastSubmittedBy as string) || undefined,
    lastSubmittedByName: (d.lastSubmittedByName as string) || undefined,
    streamRole: d.streamRole === 'supplementary' ? 'supplementary' : d.streamRole === 'primary' ? 'primary' : undefined,
    portalDisplayName: typeof d.portalDisplayName === 'string' ? d.portalDisplayName : undefined,
    ...streamStructuresFromFirestore(d.structureIds, d.structureNames),
    wholeProjectRoot: d.wholeProjectRoot === true ? true : undefined,
    forkedToStreamIds: Array.isArray(d.forkedToStreamIds)
      ? (d.forkedToStreamIds as unknown[]).map(x => String(x ?? '')).filter(Boolean)
      : undefined,
    forkedFromStreamId: typeof d.forkedFromStreamId === 'string' && d.forkedFromStreamId ? d.forkedFromStreamId : undefined,
    inheritVersionsAfter: (d.inheritVersionsAfter as { toDate?: () => Date })?.toDate?.() ?? undefined,
    structureGroupLabel:
      typeof d.structureGroupLabel === 'string' && d.structureGroupLabel.trim() ? d.structureGroupLabel.trim() : undefined,
    structureSetup: structureSetupFromFirestore(d.structureSetup),
  };
}

function structureSetupFromFirestore(raw: unknown): ProjectFileStructureSetupRecord | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const at = (r.at as { toDate?: () => Date })?.toDate?.();
  if (!(at instanceof Date)) return undefined;
  return {
    at,
    byUid: String(r.byUid ?? ''),
    byName: typeof r.byName === 'string' && r.byName ? r.byName : undefined,
    fromStreamId: typeof r.fromStreamId === 'string' && r.fromStreamId ? r.fromStreamId : undefined,
    fromStreamTitle: typeof r.fromStreamTitle === 'string' && r.fromStreamTitle ? r.fromStreamTitle : undefined,
    fileName: typeof r.fileName === 'string' && r.fileName ? r.fileName : undefined,
    createdStructure: r.createdStructure === true,
  };
}

/** Stamp a track with how "Set up structures" tied it to its structure (history card). */
export async function recordStreamStructureSetup(
  projectId: string,
  streamId: string,
  record: Omit<ProjectFileStructureSetupRecord, 'at'>,
): Promise<void> {
  const streamRef = doc(db, STREAMS_COLLECTION, streamId);
  const snap = await getDoc(streamRef);
  if (!snap.exists()) throw new Error('Version track not found');
  if (snap.data()!.projectId !== projectId) throw new Error('This version history does not belong to the project');
  const payload: Record<string, unknown> = {
    at: Timestamp.now(),
    byUid: record.byUid,
    createdStructure: record.createdStructure,
  };
  if (record.byName) payload.byName = record.byName;
  if (record.fromStreamId) payload.fromStreamId = record.fromStreamId;
  if (record.fromStreamTitle) payload.fromStreamTitle = record.fromStreamTitle;
  if (record.fileName) payload.fileName = record.fileName;
  await updateDoc(streamRef, { structureSetup: payload, updatedAt: Timestamp.now() });
}

function moveHistoryFromFirestore(raw: unknown): ProjectFileMoveRecord[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const out: ProjectFileMoveRecord[] = [];
  for (const r of raw as Record<string, unknown>[]) {
    if (!r || typeof r !== 'object') continue;
    out.push({
      fromStreamId: String(r.fromStreamId ?? ''),
      fromStreamTitle: typeof r.fromStreamTitle === 'string' ? r.fromStreamTitle : undefined,
      fromVersionIndex: typeof r.fromVersionIndex === 'number' ? r.fromVersionIndex : 0,
      toStreamId: String(r.toStreamId ?? ''),
      toVersionIndex: typeof r.toVersionIndex === 'number' ? r.toVersionIndex : 0,
      movedBy: String(r.movedBy ?? ''),
      movedByName: String(r.movedByName ?? ''),
      movedAt: (r.movedAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
    });
  }
  return out.length ? out : undefined;
}

function streamStructuresFromFirestore(
  rawIds: unknown,
  rawNames: unknown,
): Pick<ProjectFileStream, 'structureIds' | 'structureNames'> {
  if (!Array.isArray(rawIds)) return {};
  const ids = rawIds.map(x => String(x ?? '').trim()).filter(Boolean);
  if (ids.length === 0) return {};
  const names = Array.isArray(rawNames)
    ? rawNames.map(x => String(x ?? '').trim())
    : [];
  return {
    structureIds: ids,
    structureNames: ids.map((_, i) => names[i] ?? ''),
  };
}

/** CSV suitable for timber-list tagging (same rules as uploads). */
export function isCsvFile(file: Pick<File, 'name' | 'type'>): boolean {
  const t = (file.type || '').toLowerCase();
  if (t === 'text/csv' || t === 'application/csv' || t === 'application/vnd.ms-excel') return true;
  return /\.csv$/i.test(file.name || '');
}

/** All timber-list CSV project files, newest first (includes version-history rows). */
export function filterProjectTimberCsvs(files: ProjectFile[]): ProjectFile[] {
  return files
    .filter(f => f.kindTag === 'timber_list' && isCsvFile({ name: f.name, type: f.mimeType }))
    .sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime());
}

/** All hardware-list CSV project files, newest first (includes version-history rows). */
export function filterProjectHardwareCsvs(files: ProjectFile[]): ProjectFile[] {
  return files
    .filter(f => f.kindTag === 'hardware_list' && isCsvFile({ name: f.name, type: f.mimeType }))
    .sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime());
}

export async function peekCsvText(file: File): Promise<string | undefined> {
  if (!isCsvFile(file)) return undefined;
  try {
    return await file.text();
  } catch {
    return undefined;
  }
}

export function isHtmlFile(file: Pick<File, 'name' | 'type'>): boolean {
  const t = (file.type || '').toLowerCase();
  if (t === 'text/html' || t === 'application/xhtml+xml') return true;
  return /\.html?$/i.test(file.name || '');
}

function isCsvDocument(mimeType: string, fileName: string): boolean {
  const t = (mimeType || '').toLowerCase();
  if (t === 'text/csv' || t === 'application/csv') return true;
  return /\.csv$/i.test(fileName || '');
}

/** Coerce invalid tag/file pairings to a safe stored tag. */
export function normalizeKindTagForFile(
  file: Pick<File, 'name' | 'type'>,
  tag: ProjectFileKindTag,
): ProjectFileKindTag {
  if (tag === 'timber_list' && !isCsvFile(file)) return 'none';
  if (tag === 'hardware_list' && !isCsvFile(file)) return 'none';
  if (tag === 'html' && !isHtmlFile(file)) return 'none';
  return tag;
}

/** Labels shown in the pre-upload tag dialog (only valid options per file — no disabled rows). */
export function kindTagOptionsForFile(
  file: Pick<File, 'name' | 'type'>,
): { value: ProjectFileKindTag; label: string }[] {
  const opts: { value: ProjectFileKindTag; label: string }[] = [{ value: 'none', label: 'None' }];
  if (isCsvFile(file)) {
    opts.push({ value: 'timber_list', label: 'Timber list' });
    opts.push({ value: 'hardware_list', label: 'Hardware list' });
  }
  if (isHtmlFile(file)) opts.push({ value: 'html', label: 'HTML' });
  return opts;
}

/**
 * Default tag when opening the upload dialog. `.csv` → timber list; HTML files → HTML; else none.
 * Still passed through {@link normalizeKindTagForFile} on upload.
 */
export function suggestedKindTagForUpload(
  file: Pick<File, 'name' | 'type'>,
  csvText?: string,
): ProjectFileKindTag {
  if (isCsvFile(file)) {
    if ((csvText && isCadworkSteelCsv(csvText)) || filenameLooksLikeHardwareList(file.name)) {
      return 'hardware_list';
    }
    return 'timber_list';
  }
  if (isHtmlFile(file)) return 'html';
  return 'none';
}

export function isImageType(mimeType?: string): boolean {
  return !!mimeType && mimeType.startsWith('image/');
}

export function isPdfType(mimeType?: string): boolean {
  return mimeType === 'application/pdf';
}

/**
 * Lightweight CSV detector for routing UI clicks (mirrors `isCsvFile` but operates on
 * a stored `mimeType` + filename rather than a `File` object).
 */
export function isCsvType(mimeType?: string, fileName?: string): boolean {
  const t = (mimeType || '').toLowerCase();
  if (t === 'text/csv' || t === 'application/csv' || t === 'application/vnd.ms-excel') return true;
  return /\.csv$/i.test(fileName || '');
}

/** PDF, image, CSV, and HTML project files support comment pins in the viewer. */
export function projectFileSupportsCommentPins(file: Pick<ProjectFile, 'mimeType' | 'name'>): boolean {
  return (
    isPdfType(file.mimeType) ||
    isImageType(file.mimeType) ||
    isCsvType(file.mimeType, file.name) ||
    isHtmlType(file.mimeType, file.name)
  );
}

/** Resolve a project file row for Files-tab deep links (`?file=&comment=`). */
export function findProjectFileForDeepLink(
  files: ProjectFile[],
  documentId: string,
  annotationHint?: Pick<PdfAnnotation, 'documentId' | 'documentUrl'>,
): ProjectFile | undefined {
  const docId = documentId.trim();
  if (docId) {
    const direct = files.find((f) => f.id === docId || f.url === docId);
    if (direct) return direct;
    const byLeadMirror = files.find((f) => f.sourceLeadDocumentId === docId);
    if (byLeadMirror) return byLeadMirror;
    const path = normalizeStoragePath(docId);
    if (path) {
      const byPath = files.find(
        (f) =>
          normalizeStoragePath(f.storagePath || '') === path ||
          normalizeStoragePath(f.url) === path,
      );
      if (byPath) return byPath;
    }
  }
  if (annotationHint) {
    return files.find((f) => pdfAnnotationMatchesProjectFile(annotationHint, f));
  }
  return undefined;
}

export type AnnotationViewerOpenTarget = {
  url: string;
  documentId: string;
  title: string;
  mimeType?: string;
  streamId?: string;
  projectId?: string;
  storagePath?: string;
};

function isHttpUrl(raw: string): boolean {
  return /^https?:\/\//i.test(raw.trim());
}

/**
 * Resolve a live download URL for a PDF/image comment pin.
 *
 * Annotation `documentUrl` can be a stale Firebase download token (or a bare
 * document id). Opening that string in the viewer yields pdf.js "Invalid PDF".
 * Prefer the current `projectFiles/{id}.url` (same path the Files tab uses).
 */
export async function resolveAnnotationViewerOpenTarget(args: {
  documentId?: string | null;
  documentUrl?: string | null;
  title?: string | null;
}): Promise<AnnotationViewerOpenTarget | null> {
  const documentId = (args.documentId || '').trim();
  const documentUrl = (args.documentUrl || '').trim();
  const titleHint = (args.title || '').trim();

  const fromFile = (file: ProjectFile): AnnotationViewerOpenTarget => ({
    url: file.url,
    documentId: (file.id || documentId || file.url).trim(),
    title: (file.name || titleHint || displayNameFromFileRef(file.url || documentUrl)).trim() || 'Document',
    mimeType: file.mimeType,
    streamId: file.streamId,
    projectId: file.projectId,
    storagePath: file.storagePath,
  });

  if (documentId) {
    try {
      const snap = await getDoc(doc(db, COLLECTION, documentId));
      if (snap.exists()) {
        return fromFile(fromFirestore({ id: snap.id, data: () => snap.data() as Record<string, unknown> }));
      }
    } catch {
      /* fall through — try url / path matches */
    }
  }

  const candidates = [documentUrl, documentId].filter(Boolean);
  for (const candidate of candidates) {
    const path = normalizeStoragePath(candidate);
    const looksLikeProjectFile =
      isHttpUrl(candidate) || path.startsWith('projectFiles/');
    if (!looksLikeProjectFile) continue;
    try {
      if (isHttpUrl(candidate)) {
        const byUrl = await getDocs(
          query(collection(db, COLLECTION), where('url', '==', candidate), limit(1)),
        );
        if (!byUrl.empty) {
          const snap = byUrl.docs[0];
          return fromFile(fromFirestore({ id: snap.id, data: () => snap.data() as Record<string, unknown> }));
        }
      }

      if (path.startsWith('projectFiles/')) {
        const byPath = await getDocs(
          query(collection(db, COLLECTION), where('storagePath', '==', path), limit(1)),
        );
        if (!byPath.empty) {
          const snap = byPath.docs[0];
          return fromFile(fromFirestore({ id: snap.id, data: () => snap.data() as Record<string, unknown> }));
        }
      }
    } catch {
      /* continue */
    }
  }

  // Lead-doc mirrors: annotation may store the lead document id as documentId.
  if (documentId) {
    try {
      const byLead = await getDocs(
        query(collection(db, COLLECTION), where('sourceLeadDocumentId', '==', documentId), limit(1)),
      );
      if (!byLead.empty) {
        const snap = byLead.docs[0];
        return fromFile(fromFirestore({ id: snap.id, data: () => snap.data() as Record<string, unknown> }));
      }
    } catch {
      /* fall through */
    }
  }

  // Last resort: only open a real http(s) URL — never a bare Firestore id (pdf.js → "Invalid PDF").
  if (isHttpUrl(documentUrl)) {
    return {
      url: documentUrl,
      documentId: documentId || documentUrl,
      title: titleHint || displayNameFromFileRef(documentUrl),
    };
  }

  return null;
}

const QUOTE_TYPES: ProjectQuoteType[] = ['timber', 'cnc', 'timber_and_cnc', 'hardware_and_fasteners'];

export function isProjectQuoteFile(file: Pick<ProjectFile, 'fileSection' | 'streamId'>): boolean {
  return file.fileSection === 'quote' && !file.streamId;
}

const QUOTE_TYPE_LABELS: Record<ProjectQuoteType, string> = {
  timber: 'Timber',
  cnc: 'CNC',
  timber_and_cnc: 'Timber & CNC',
  hardware_and_fasteners: 'Hardware & Fasteners',
};

export function quoteTypeLabel(type: ProjectQuoteType): string {
  return QUOTE_TYPE_LABELS[type] ?? type;
}

function coerceQuoteType(raw: unknown): ProjectQuoteType | undefined {
  return typeof raw === 'string' && QUOTE_TYPES.includes(raw as ProjectQuoteType)
    ? (raw as ProjectQuoteType)
    : undefined;
}

function coerceQuoteStatus(raw: unknown): ProjectQuoteStatus | undefined {
  return raw === 'pending' || raw === 'approved' ? raw : undefined;
}

function cncStructuresFromFirestore(
  rawIds: unknown,
  rawNames: unknown,
): { cncStructureIds?: string[]; cncStructureNames?: string[] } {
  if (!Array.isArray(rawIds)) return {};
  const names = Array.isArray(rawNames) ? rawNames : [];
  const paired = rawIds
    .map((id, index) => ({
      id: String(id ?? ''),
      name: String(names[index] ?? id ?? ''),
    }))
    .filter((row) => row.id);
  if (paired.length === 0) return {};
  return {
    cncStructureIds: paired.map((row) => row.id),
    cncStructureNames: paired.map((row) => row.name),
  };
}

function fromFirestore(snap: { id: string; data: () => Record<string, unknown> }): ProjectFile {
  const d = snap.data();
  const kindTag = (d.kindTag as ProjectFileKindTag | undefined) || undefined;
  const fileSection = d.fileSection === 'quote' ? ('quote' as ProjectFileSection) : undefined;
  const quoteType = coerceQuoteType(d.quoteType);
  const quoteStatus = coerceQuoteStatus(d.quoteStatus);
  return {
    id: snap.id,
    projectId: d.projectId as string,
    name: d.name as string,
    url: d.url as string,
    storagePath: d.storagePath as string,
    mimeType: (d.mimeType as string) || 'application/octet-stream',
    size: d.size as number,
    uploadedAt: (d.uploadedAt as { toDate: () => Date })?.toDate?.() ?? new Date(),
    uploadedBy: d.uploadedBy as string,
    uploadedByName: (d.uploadedByName as string) || '',
    kindTag:
      kindTag === 'timber_list' || kindTag === 'hardware_list' || kindTag === 'html' || kindTag === 'none'
        ? kindTag
        : undefined,
    isCurrentTimberList: d.isCurrentTimberList === true,
    isCurrentHardwareList: d.isCurrentHardwareList === true,
    streamId: (d.streamId as string) || undefined,
    folderPath:
      typeof d.folderPath === 'string' && d.folderPath.trim() !== '' ? d.folderPath.trim() : undefined,
    versionIndex: typeof d.versionIndex === 'number' ? d.versionIndex : undefined,
    versionLabel: (d.versionLabel as string) || undefined,
    versionNote: (d.versionNote as string) || undefined,
    detailStage: parseProjectFileDetailStage(d.detailStage),
    trueDimensions: d.trueDimensions === true ? true : undefined,
    forCnc: d.forCnc === true ? true : undefined,
    ...cncStructuresFromFirestore(d.cncStructureIds, d.cncStructureNames),
    isHeadVersion: d.isHeadVersion === true,
    wholeProjectOrigin:
      d.wholeProjectOrigin && typeof d.wholeProjectOrigin === 'object'
        ? {
            streamId: String((d.wholeProjectOrigin as Record<string, unknown>).streamId ?? ''),
            fileId: String((d.wholeProjectOrigin as Record<string, unknown>).fileId ?? ''),
          }
        : undefined,
    uploadedAsWholeProject: d.uploadedAsWholeProject === true ? true : undefined,
    structureReleaseId:
      typeof d.structureReleaseId === 'string' && d.structureReleaseId.trim() !== ''
        ? d.structureReleaseId.trim()
        : undefined,
    sharedOnStreamIds: Array.isArray(d.sharedOnStreamIds)
      ? (d.sharedOnStreamIds as unknown[]).map(id => String(id ?? '').trim()).filter(Boolean)
      : undefined,
    moveHistory: moveHistoryFromFirestore(d.moveHistory),
    htmlModelDescriptors: Array.isArray(d.htmlModelDescriptors)
      ? (d.htmlModelDescriptors as unknown[]).map((x) => String(x ?? '')).filter(Boolean)
      : undefined,
    htmlPreviewImageUrl:
      typeof d.htmlPreviewImageUrl === 'string' && d.htmlPreviewImageUrl.trim() !== ''
        ? d.htmlPreviewImageUrl.trim()
        : undefined,
    fileSection,
    quoteType,
    quoteStatus,
    quoteApprovedAt: (d.quoteApprovedAt as { toDate: () => Date })?.toDate?.() ?? undefined,
    quoteApprovedBy: (d.quoteApprovedBy as string) || undefined,
    quoteApprovedByName: (d.quoteApprovedByName as string) || undefined,
    quoteBillingNotifyUserId: (d.quoteBillingNotifyUserId as string) || undefined,
    quoteBillingNotifyUserName: (d.quoteBillingNotifyUserName as string) || undefined,
    sourceLeadDocumentId: (d.sourceLeadDocumentId as string) || undefined,
  };
}

/** PDF, HTML, timber CSV, or native 3D (Cadwork, SketchUp, IFC, STEP, DXF, SAT) — suitable for version tracks (not photos/general docs). */
export function isVersionTrackCandidateFile(file: Pick<File, 'name' | 'type'>): boolean {
  return (
    isPdfType(file.type || '') ||
    isHtmlFile(file) ||
    isCsvFile(file) ||
    isThreeDRawFile(file)
  );
}

/** Default stream kind from file MIME/name when creating a new version history. */
export function suggestedStreamKindForFile(
  file: Pick<File, 'name' | 'type'>,
  csvText?: string,
): ProjectFileStreamKind {
  if (isThreeDRawFile(file)) return 'three_d_raw';
  if (isPdfType(file.type || '')) return 'two_d_pdf';
  if (isHtmlFile(file)) return 'three_d_html';
  if (isCsvFile(file)) return csvListStreamKind(file, csvText);
  return 'general';
}

/** Accept attribute for picking PDF / HTML / CSV / native 3D in one control. */
export const VERSION_BATCH_FILE_ACCEPT =
  `application/pdf,.pdf,text/html,.html,.htm,application/xhtml+xml,text/csv,.csv,application/csv,application/vnd.ms-excel,${THREE_D_RAW_ACCEPT}`;

/** General project documents — no extension or MIME filter (Cadwork .cwlm, Office, etc.). */
export const GENERAL_BATCH_FILE_ACCEPT = '*/*';

/** MIME matches version-track kind for uploads. */
export function fileMatchesStreamKind(
  mimeType: string,
  fileName: string,
  streamKind: ProjectFileStreamKind,
): boolean {
  if (streamKind === 'three_d_html') return isHtmlFile({ name: fileName, type: mimeType });
  if (streamKind === 'three_d_raw') return isThreeDRawFile({ name: fileName, type: mimeType });
  if (streamKind === 'two_d_pdf') return isPdfType(mimeType);
  if (streamKind === 'hardware_list_csv' || streamKind === 'timber_list_csv') {
    return isCsvFile({ name: fileName, type: mimeType });
  }
  return true;
}

function kindTagForTrackKind(
  streamKind: ProjectFileStreamKind,
  file: Pick<File, 'name' | 'type'>,
): ProjectFileKindTag {
  if (streamKind === 'three_d_html') return normalizeKindTagForFile(file, 'html');
  if (streamKind === 'timber_list_csv') return normalizeKindTagForFile(file, 'timber_list');
  if (streamKind === 'hardware_list_csv') return normalizeKindTagForFile(file, 'hardware_list');
  return 'none';
}

async function supersedePreviousTimberLists(projectId: string, newFileId: string): Promise<void> {
  const snap = await getDocs(query(collection(db, COLLECTION), where('projectId', '==', projectId)));
  const updates: Promise<unknown>[] = [];
  for (const d of snap.docs) {
    if (d.id === newFileId) continue;
    const data = d.data();
    if (
      data.kindTag === 'timber_list' &&
      isCsvDocument(String(data.mimeType || ''), String(data.name || '')) &&
      data.isCurrentTimberList === true
    ) {
      updates.push(updateDoc(doc(db, COLLECTION, d.id), { isCurrentTimberList: false }));
    }
  }
  await Promise.all(updates);
}

async function syncProposalTimberListPrompt(
  dealId: string,
  fileId: string,
  fileName: string,
  fileUrl: string,
  uploadedByName: string,
  uploadedBy: string,
): Promise<void> {
  await setDoc(
    doc(db, 'proposalBuilders', dealId),
    {
      latestTimberListPrompt: {
        fileId,
        fileName,
        fileUrl,
        uploadedAt: Timestamp.now(),
        uploadedByName: uploadedByName || '',
        uploadedBy,
      },
      timberListBannerDismissedForFileId: deleteField(),
    },
    { merge: true },
  );
}

/**
 * After project files change (e.g. delete), re-point proposal prompt + isCurrentTimberList to the newest timber list CSV, or clear.
 */
export async function reconcileTimberListStateForProject(
  projectId: string,
  dealId: string | undefined,
): Promise<void> {
  const snap = await getDocs(query(collection(db, COLLECTION), where('projectId', '==', projectId)));
  const rows = snap.docs.map(d => ({ id: d.id, ...d.data() })) as Array<{
    id: string;
    kindTag?: string;
    mimeType?: string;
    name?: string;
    uploadedAt?: { toDate: () => Date };
    isCurrentTimberList?: boolean;
    url?: string;
  }>;

  const timberCsv = rows.filter(
    r =>
      r.kindTag === 'timber_list' &&
      isCsvDocument(String(r.mimeType || ''), String(r.name || '')),
  );
  timberCsv.sort((a, b) => {
    const ta = a.uploadedAt?.toDate?.()?.getTime() ?? 0;
    const tb = b.uploadedAt?.toDate?.()?.getTime() ?? 0;
    return tb - ta;
  });

  const updates: Promise<unknown>[] = [];
  for (const r of rows) {
    const shouldBeCurrent =
      timberCsv.length > 0 &&
      r.id === timberCsv[0].id &&
      r.kindTag === 'timber_list' &&
      isCsvDocument(String(r.mimeType || ''), String(r.name || ''));
    const want = shouldBeCurrent === true;
    if (r.isCurrentTimberList !== want) {
      updates.push(updateDoc(doc(db, COLLECTION, r.id), { isCurrentTimberList: want }));
    }
  }
  await Promise.all(updates);

  if (!dealId) return;

  if (timberCsv.length === 0) {
    await setDoc(
      doc(db, 'proposalBuilders', dealId),
      {
        latestTimberListPrompt: deleteField(),
        timberListBannerDismissedForFileId: deleteField(),
      },
      { merge: true },
    );
    return;
  }

  const top = timberCsv[0] as {
    id: string;
    name?: string;
    url?: string;
    uploadedAt?: { toDate?: () => Date };
    uploadedByName?: string;
    uploadedBy?: string;
  };
  await setDoc(
    doc(db, 'proposalBuilders', dealId),
    {
      latestTimberListPrompt: {
        fileId: top.id,
        fileName: String(top.name || 'timber-list.csv'),
        fileUrl: String(top.url || ''),
        uploadedAt: top.uploadedAt ?? Timestamp.now(),
        uploadedByName: String(top.uploadedByName || ''),
        uploadedBy: String(top.uploadedBy || ''),
      },
      timberListBannerDismissedForFileId: deleteField(),
    },
    { merge: true },
  );
}

async function supersedePreviousHardwareLists(projectId: string, newFileId: string): Promise<void> {
  const snap = await getDocs(query(collection(db, COLLECTION), where('projectId', '==', projectId)));
  const updates: Promise<unknown>[] = [];
  for (const d of snap.docs) {
    if (d.id === newFileId) continue;
    const data = d.data();
    if (
      data.kindTag === 'hardware_list' &&
      isCsvDocument(String(data.mimeType || ''), String(data.name || '')) &&
      data.isCurrentHardwareList === true
    ) {
      updates.push(updateDoc(doc(db, COLLECTION, d.id), { isCurrentHardwareList: false }));
    }
  }
  await Promise.all(updates);
}

async function syncProposalHardwareListPrompt(
  dealId: string,
  fileId: string,
  fileName: string,
  fileUrl: string,
  uploadedByName: string,
  uploadedBy: string,
): Promise<void> {
  await setDoc(
    doc(db, 'proposalBuilders', dealId),
    {
      latestHardwareListPrompt: {
        fileId,
        fileName,
        fileUrl,
        uploadedAt: Timestamp.now(),
        uploadedByName: uploadedByName || '',
        uploadedBy,
      },
      hardwareListBannerDismissedForFileId: deleteField(),
    },
    { merge: true },
  );
}

/**
 * After project files change (e.g. delete), re-point proposal prompt + isCurrentHardwareList
 * to the newest hardware list CSV, or clear.
 */
export async function reconcileHardwareListStateForProject(
  projectId: string,
  dealId: string | undefined,
): Promise<void> {
  const snap = await getDocs(query(collection(db, COLLECTION), where('projectId', '==', projectId)));
  const rows = snap.docs.map(d => ({ id: d.id, ...d.data() })) as Array<{
    id: string;
    kindTag?: string;
    mimeType?: string;
    name?: string;
    uploadedAt?: { toDate: () => Date };
    isCurrentHardwareList?: boolean;
    url?: string;
  }>;

  const hardwareCsv = rows.filter(
    r =>
      r.kindTag === 'hardware_list' &&
      isCsvDocument(String(r.mimeType || ''), String(r.name || '')),
  );
  hardwareCsv.sort((a, b) => {
    const ta = a.uploadedAt?.toDate?.()?.getTime() ?? 0;
    const tb = b.uploadedAt?.toDate?.()?.getTime() ?? 0;
    return tb - ta;
  });

  const updates: Promise<unknown>[] = [];
  for (const r of rows) {
    const shouldBeCurrent =
      hardwareCsv.length > 0 &&
      r.id === hardwareCsv[0].id &&
      r.kindTag === 'hardware_list' &&
      isCsvDocument(String(r.mimeType || ''), String(r.name || ''));
    const want = shouldBeCurrent === true;
    if (r.isCurrentHardwareList !== want) {
      updates.push(updateDoc(doc(db, COLLECTION, r.id), { isCurrentHardwareList: want }));
    }
  }
  await Promise.all(updates);

  if (!dealId) return;

  if (hardwareCsv.length === 0) {
    await setDoc(
      doc(db, 'proposalBuilders', dealId),
      {
        latestHardwareListPrompt: deleteField(),
        hardwareListBannerDismissedForFileId: deleteField(),
      },
      { merge: true },
    );
    return;
  }

  const top = hardwareCsv[0] as {
    id: string;
    name?: string;
    url?: string;
    uploadedAt?: { toDate?: () => Date };
    uploadedByName?: string;
    uploadedBy?: string;
  };
  await setDoc(
    doc(db, 'proposalBuilders', dealId),
    {
      latestHardwareListPrompt: {
        fileId: top.id,
        fileName: String(top.name || 'hardware-list.csv'),
        fileUrl: String(top.url || ''),
        uploadedAt: top.uploadedAt ?? Timestamp.now(),
        uploadedByName: String(top.uploadedByName || ''),
        uploadedBy: String(top.uploadedBy || ''),
      },
      hardwareListBannerDismissedForFileId: deleteField(),
    },
    { merge: true },
  );
}

export type UploadProjectFileOptions = {
  kindTag?: ProjectFileKindTag;
  /** When set and upload is a timber-list CSV, updates proposal builder banner for this deal. */
  dealId?: string;
  fileSection?: ProjectFileSection;
  quoteType?: ProjectQuoteType;
  /** Virtual project folder (see `projectFileFolders.ts`). Omit for Unsorted. */
  folderPath?: string;
};

async function prepareTimberListUploadPayload(
  file: File,
  isTimberListCsv: boolean,
): Promise<{ payload: Blob | File; size: number }> {
  if (!isTimberListCsv) {
    return { payload: file, size: file.size };
  }
  const text = await file.text();
  const enriched = enrichTimberCsvText(text);
  const blob = new Blob([enriched], { type: file.type || 'text/csv' });
  return { payload: blob, size: blob.size };
}

export async function uploadProjectFile(
  projectId: string,
  file: File,
  uploadedBy: string,
  uploadedByName: string,
  options?: UploadProjectFileOptions,
): Promise<ProjectFile> {
  const kindTag = normalizeKindTagForFile(file, options?.kindTag ?? 'none');
  const isTimberListCsv = kindTag === 'timber_list' && isCsvFile(file);
  const isHardwareListCsv = kindTag === 'hardware_list' && isCsvFile(file);
  const isQuote = options?.fileSection === 'quote';
  const quoteType = isQuote ? options?.quoteType : undefined;
  if (isQuote && !quoteType) {
    throw new Error('Quote type is required for quote uploads.');
  }
  const uploadBlock = projectFileUploadBlockReason(file);
  if (uploadBlock) throw new Error(uploadBlock);
  if (isFinancialsFolderPath(options?.folderPath)) {
    throw new Error('Financials files are stored separately from other project files.');
  }
  const folderPath =
    !isQuote && options?.folderPath && isWritableProjectFolderPath(options.folderPath)
      ? options.folderPath
      : undefined;

  const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const storagePath = `${STORAGE_PREFIX}/${projectId}/${Date.now()}-${safeName}`;
  const storageRef = ref(storage, storagePath);
  await ensureCrmStorageAccessBeforeUpload();
  const { payload, size: uploadSize } = await prepareTimberListUploadPayload(file, isTimberListCsv);
  await uploadBytes(storageRef, payload, { contentType: file.type || 'application/octet-stream' });
  const url = await getDownloadURL(storageRef);

  const docRef = await addDoc(collection(db, COLLECTION), {
    projectId,
    name: file.name,
    url,
    storagePath,
    mimeType: file.type || 'application/octet-stream',
    size: uploadSize,
    uploadedAt: Timestamp.now(),
    uploadedBy,
    uploadedByName,
    kindTag,
    isCurrentTimberList: isTimberListCsv,
    isCurrentHardwareList: isHardwareListCsv,
    ...(folderPath ? { folderPath } : {}),
    ...(isQuote
      ? {
          fileSection: 'quote' as const,
          quoteType,
          quoteStatus: 'pending' as const,
        }
      : {}),
  });

  if (isTimberListCsv) {
    await supersedePreviousTimberLists(projectId, docRef.id);
  }
  if (isHardwareListCsv) {
    await supersedePreviousHardwareLists(projectId, docRef.id);
  }

  const dealId = options?.dealId;
  if (isTimberListCsv && dealId) {
    await syncProposalTimberListPrompt(dealId, docRef.id, file.name, url, uploadedByName, uploadedBy);
  }
  if (isHardwareListCsv && dealId) {
    await syncProposalHardwareListPrompt(dealId, docRef.id, file.name, url, uploadedByName, uploadedBy);
  }

  return {
    id: docRef.id,
    projectId,
    name: file.name,
    url,
    storagePath,
    mimeType: file.type || 'application/octet-stream',
    size: uploadSize,
    uploadedAt: new Date(),
    uploadedBy,
    uploadedByName,
    kindTag,
    isCurrentTimberList: isTimberListCsv,
    isCurrentHardwareList: isHardwareListCsv,
    ...(folderPath ? { folderPath } : {}),
    ...(isQuote
      ? {
          fileSection: 'quote' as const,
          quoteType,
          quoteStatus: 'pending' as const,
        }
      : {}),
  };
}

function financialFileFromFirestore(snap: { id: string; data: () => Record<string, unknown> }): ProjectFile {
  return {
    ...fromFirestore(snap),
    employeeOnly: true,
    folderPath: FINANCIALS_FOLDER_PATH,
    streamId: undefined,
    fileSection: undefined,
  };
}

/**
 * Employee-only Financials upload. Stored outside `projectFiles` so architects,
 * designers, and other partners cannot list the download URL.
 */
export async function uploadProjectFinancialFile(
  projectId: string,
  file: File,
  uploadedBy: string,
  uploadedByName: string,
): Promise<ProjectFile> {
  const uploadBlock = projectFileUploadBlockReason(file);
  if (uploadBlock) throw new Error(uploadBlock);
  const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const storagePath = `${FINANCIAL_STORAGE_PREFIX}/${projectId}/${Date.now()}-${safeName}`;
  const storageRef = ref(storage, storagePath);
  await ensureCrmStorageAccessBeforeUpload();
  await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });
  const url = await getDownloadURL(storageRef);
  const docRef = await addDoc(collection(db, FINANCIAL_COLLECTION), {
    projectId,
    name: file.name,
    url,
    storagePath,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    uploadedAt: Timestamp.now(),
    uploadedBy,
    uploadedByName,
    folderPath: FINANCIALS_FOLDER_PATH,
    employeeOnly: true,
  });
  return {
    id: docRef.id,
    projectId,
    name: file.name,
    url,
    storagePath,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    uploadedAt: new Date(),
    uploadedBy,
    uploadedByName,
    folderPath: FINANCIALS_FOLDER_PATH,
    employeeOnly: true,
  };
}

export function subscribeProjectFinancialFiles(
  projectId: string,
  callback: (files: ProjectFile[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(collection(db, FINANCIAL_COLLECTION), where('projectId', '==', projectId));
  return onSnapshot(
    q,
    snap => {
      const files = snap.docs.map(d => financialFileFromFirestore({ id: d.id, data: () => d.data() }));
      files.sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime());
      callback(files);
    },
    err => {
      console.error('[projectFiles] financials subscribe', err);
      onError?.(err);
    },
  );
}

export async function deleteProjectFinancialFile(fileId: string, storagePath: string): Promise<void> {
  try {
    if (storagePath) await deleteObject(ref(storage, storagePath));
  } catch {
    // file may already be gone
  }
  await deleteDoc(doc(db, FINANCIAL_COLLECTION, fileId));
}

/**
 * Move an unversioned file between project folders.
 * Crossing into or out of Financials copies the Firestore doc between collections
 * and leaves the storage object in place.
 */
export async function moveUnversionedProjectFile(
  projectId: string,
  fileId: string,
  folderPath: string | null,
  source: 'project' | 'financials',
): Promise<void> {
  const toFinancials = isFinancialsFolderPath(folderPath);
  if (toFinancials && folderPath !== FINANCIALS_FOLDER_PATH) {
    throw new Error('Unknown project folder');
  }
  if (!toFinancials && folderPath && !isWritableProjectFolderPath(folderPath)) {
    throw new Error('Unknown project folder');
  }
  const srcCol = source === 'financials' ? FINANCIAL_COLLECTION : COLLECTION;
  const fileRef = doc(db, srcCol, fileId);
  const snap = await getDoc(fileRef);
  if (!snap.exists()) throw new Error('File not found');
  const fd = snap.data()!;
  if (fd.projectId !== projectId) throw new Error('This file does not belong to the project');
  if (fd.streamId) throw new Error('Versioned files cannot be moved into folders');

  const fromFinancials = source === 'financials';
  if (fromFinancials === toFinancials) {
    await updateDoc(fileRef, {
      folderPath: toFinancials ? FINANCIALS_FOLDER_PATH : folderPath ? folderPath : deleteField(),
    });
    return;
  }

  const destCol = toFinancials ? FINANCIAL_COLLECTION : COLLECTION;
  const destRef = doc(db, destCol, fileId);
  if (toFinancials) {
    await setDoc(destRef, {
      projectId: fd.projectId,
      name: fd.name,
      url: fd.url,
      storagePath: fd.storagePath,
      mimeType: fd.mimeType || 'application/octet-stream',
      size: fd.size ?? 0,
      uploadedAt: fd.uploadedAt ?? Timestamp.now(),
      uploadedBy: fd.uploadedBy,
      uploadedByName: fd.uploadedByName || '',
      ...(fd.kindTag ? { kindTag: fd.kindTag } : {}),
      folderPath: FINANCIALS_FOLDER_PATH,
      employeeOnly: true,
    });
  } else {
    const next: Record<string, unknown> = { ...fd };
    delete next.employeeOnly;
    if (folderPath) next.folderPath = folderPath;
    else delete next.folderPath;
    await setDoc(destRef, next);
  }
  await deleteDoc(fileRef);
}

/**
 * Move an unversioned project file into a virtual folder (or back to Unsorted with `null`).
 * Versioned rows (`streamId`) are never foldered — they live in Deliverables.
 */
export async function moveProjectFileToFolder(
  projectId: string,
  fileId: string,
  folderPath: string | null,
): Promise<void> {
  const fileRef = doc(db, COLLECTION, fileId);
  const snap = await getDoc(fileRef);
  if (!snap.exists()) throw new Error('File not found');
  const fd = snap.data()!;
  if (fd.projectId !== projectId) throw new Error('This file does not belong to the project');
  if (fd.streamId) throw new Error('Versioned files cannot be moved into folders');
  if (isFinancialsFolderPath(folderPath)) {
    throw new Error('Move Financials files with the project folder control.');
  }
  if (folderPath && !isWritableProjectFolderPath(folderPath)) {
    throw new Error('Unknown project folder');
  }
  await updateDoc(fileRef, {
    folderPath: folderPath ? folderPath : deleteField(),
  });
}

export async function approveProjectQuote(
  fileId: string,
  billingNotifyUserId: string,
): Promise<void> {
  const functions = getFunctions(undefined, 'us-central1');
  const fn = httpsCallable<
    { fileId: string; billingNotifyUserId: string },
    { ok: boolean }
  >(functions, 'approveProjectQuote');
  await fn({ fileId, billingNotifyUserId });
}

export async function createProjectFileStream(
  projectId: string,
  title: string,
  streamKind: ProjectFileStreamKind,
  userId: string,
  description?: string,
  options?: {
    streamRole?: 'primary' | 'supplementary';
    portalDisplayName?: string;
    /** Proposal structures this track covers; omit / empty = whole project. */
    structures?: { id: string; name: string }[];
    /** Files-rail group name, when this track is created for a renamed structure group. */
    structureGroupLabel?: string;
  },
): Promise<ProjectFileStream> {
  const payload: Record<string, unknown> = {
    projectId,
    title: title.trim() || 'Untitled',
    streamKind,
    description: (description || '').trim(),
    createdAt: Timestamp.now(),
    createdBy: userId,
    updatedAt: Timestamp.now(),
    lastVersionIndex: 0,
    headFileId: null,
  };
  const structures = normalizeStructureRefs(options?.structures);
  if (structures.length > 0) {
    payload.structureIds = structures.map(s => s.id);
    payload.structureNames = structures.map(s => s.name);
  }
  if (options?.structureGroupLabel?.trim()) {
    payload.structureGroupLabel = options.structureGroupLabel.trim();
  }
  if (options?.streamRole === 'supplementary') {
    payload.streamRole = 'supplementary';
    if (options.portalDisplayName?.trim()) {
      payload.portalDisplayName = options.portalDisplayName.trim();
    }
  } else if (options?.streamRole === 'primary') {
    payload.streamRole = 'primary';
  }
  const docRef = await addDoc(collection(db, STREAMS_COLLECTION), payload);
  const snap = await getDoc(doc(db, STREAMS_COLLECTION, docRef.id));
  if (!snap.exists()) throw new Error('Failed to create version track');
  return streamFromFirestore({ id: snap.id, data: () => snap.data()! });
}

/**
 * True when the track was created but no file has been published to it yet
 * (no head; monotonic index still 0). Server-side delete also requires zero `projectFiles` rows.
 */
export function isVersionTrackWithNoFiles(s: ProjectFileStream): boolean {
  return !s.headFileId && (s.lastVersionIndex ?? 0) === 0;
}

/** Remove a version track that has no file versions. Fails if any `projectFiles` row references the track. */
export async function deleteProjectFileStream(projectId: string, streamId: string): Promise<void> {
  const streamRef = doc(db, STREAMS_COLLECTION, streamId);
  const streamSnap = await getDoc(streamRef);
  if (!streamSnap.exists()) return;
  const sd = streamSnap.data()!;
  if (sd.projectId !== projectId) {
    throw new Error('This version history does not belong to the project');
  }
  const versionQ = query(
    collection(db, COLLECTION),
    where('projectId', '==', projectId),
    where('streamId', '==', streamId),
  );
  const hasFiles = await getDocs(versionQ);
  if (!hasFiles.empty) {
    throw new Error('This version history has files. Remove all versions from the timeline first.');
  }
  await deleteDoc(streamRef);
}

/** Rename a version history track (display name in the sidebar and panel header). */
export async function renameProjectFileStream(
  projectId: string,
  streamId: string,
  title: string,
): Promise<void> {
  const streamRef = doc(db, STREAMS_COLLECTION, streamId);
  const streamSnap = await getDoc(streamRef);
  if (!streamSnap.exists()) throw new Error('Version track not found');
  const sd = streamSnap.data()!;
  if (sd.projectId !== projectId) {
    throw new Error('This version history does not belong to the project');
  }
  const t = title.trim();
  if (!t) throw new Error('Name is required');
  await updateDoc(streamRef, {
    title: t,
    updatedAt: Timestamp.now(),
  });
}

function normalizeStructureRefs(
  input: { id: string; name: string }[] | undefined,
): { id: string; name: string }[] {
  if (!input) return [];
  const seen = new Set<string>();
  const out: { id: string; name: string }[] = [];
  for (const s of input) {
    const id = String(s?.id ?? '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, name: String(s?.name ?? '').trim() });
  }
  return out;
}

/**
 * Tag (or untag) a deliverable track with proposal structures. Pass an empty list to make
 * the track cover the whole project again. Does not touch any version rows or releases.
 */
/**
 * Rename a structure group (rail section). The label lives on every track in the
 * group so the heading survives tracks being added or removed; an empty label
 * clears the custom name and the heading falls back to the structure names.
 */
export async function renameProjectFileStructureGroup(
  projectId: string,
  streamIds: string[],
  label: string,
): Promise<void> {
  const ids = Array.from(new Set(streamIds.filter(Boolean)));
  if (ids.length === 0) return;
  const t = label.trim();
  const snaps = await Promise.all(ids.map(id => getDoc(doc(db, STREAMS_COLLECTION, id))));
  for (const s of snaps) {
    if (!s.exists()) throw new Error('Version track not found');
    if (s.data()!.projectId !== projectId) throw new Error('This version history does not belong to the project');
  }
  const parsed = snaps.map(s => streamFromFirestore({ id: s.id, data: () => s.data()! as Record<string, unknown> }));
  const structureNames = (parsed[0]?.structureNames ?? []).map(n => (n || '').trim()).filter(Boolean);
  const nameLabels = structureNamePrefixLabels(structureNames);
  const previousCustom = parsed.map(s => (s.structureGroupLabel || '').trim()).find(Boolean) || '';
  const auto = structureNames.length ? structureSelectionLabel(structureNames, { maxNamed: 3 }) : '';
  const oldLabels = [previousCustom, ...nameLabels];
  const nextLabel = t || auto;
  const batch = writeBatch(db);
  const now = Timestamp.now();
  parsed.forEach((stream, i) => {
    const nextTitle = nextLabel ? replaceStreamTitlePrefix(stream.title, oldLabels, nextLabel) : null;
    batch.update(snaps[i].ref, {
      structureGroupLabel: t || deleteField(),
      ...(nextTitle ? { title: nextTitle } : {}),
      updatedAt: now,
    });
  });
  await batch.commit();
}

export async function updateProjectFileStreamStructures(
  projectId: string,
  streamId: string,
  structures: { id: string; name: string }[],
): Promise<void> {
  const streamRef = doc(db, STREAMS_COLLECTION, streamId);
  const streamSnap = await getDoc(streamRef);
  if (!streamSnap.exists()) throw new Error('Version track not found');
  const sd = streamSnap.data()!;
  if (sd.projectId !== projectId) {
    throw new Error('This version history does not belong to the project');
  }
  const normalized = normalizeStructureRefs(structures);
  const stream = streamFromFirestore({ id: streamSnap.id, data: () => sd as Record<string, unknown> });
  const oldKey = structureSetKey(stream.structureIds ?? []);
  const newKey = structureSetKey(normalized.map(s => s.id));
  const newNames = normalized.map(s => s.name).filter(Boolean);
  const nextPrefix = newNames.length ? structureSelectionLabel(newNames, { maxNamed: 3 }) : '';
  const nextTitle = replaceStreamTitlePrefix(stream.title, structureNamePrefixLabels(stream.structureNames), nextPrefix);
  await updateDoc(streamRef, {
    structureIds: normalized.length ? normalized.map(s => s.id) : deleteField(),
    structureNames: normalized.length ? normalized.map(s => s.name) : deleteField(),
    ...(nextTitle ? { title: nextTitle } : {}),
    ...(oldKey !== newKey ? { structureGroupLabel: deleteField() } : {}),
    updatedAt: Timestamp.now(),
  });
}

/** Change the display label for one revision in the timeline (e.g. “Baker (1)”, “Issued for review”). */
export async function updateProjectFileVersionLabel(
  projectId: string,
  fileId: string,
  versionLabel: string,
): Promise<void> {
  const fileRef = doc(db, COLLECTION, fileId);
  const snap = await getDoc(fileRef);
  if (!snap.exists()) throw new Error('Version not found');
  const fd = snap.data()!;
  if (fd.projectId !== projectId) {
    throw new Error('This file does not belong to the project');
  }
  const vt = versionLabel.trim();
  if (!vt) throw new Error('Version name is required');
  await updateDoc(fileRef, { versionLabel: vt });
}

/** Update a version’s label, LOD group, and, for a CNC 3D model, which structures it covers. */
export async function updateProjectFileVersionEdit(
  projectId: string,
  fileId: string,
  patch: {
    versionLabel: string;
    /** Empty string clears the group so a historic version stays outside the LOD sections. */
    detailStage?: ProjectFileDetailStage | '';
    /** Timber CSV only. False clears the flag so later reads round the cross-section. */
    trueDimensions?: boolean;
    forCnc?: boolean;
    cncStructureIds?: string[];
    cncStructureNames?: string[];
    cncReleaseFrom?: { fileId: string; structureIds: string[] }[];
  },
): Promise<void> {
  const vt = patch.versionLabel.trim();
  if (!vt) throw new Error('Version name is required');
  const fileRef = doc(db, COLLECTION, fileId);

  await runTransaction(db, async transaction => {
    const snap = await transaction.get(fileRef);
    if (!snap.exists()) throw new Error('Version not found');
    const fd = snap.data()!;
    if (fd.projectId !== projectId) throw new Error('This file does not belong to the project');

    const releaseReads: {
      fileId: string;
      structureIds: string[];
      ref: ReturnType<typeof doc>;
      snap: Awaited<ReturnType<typeof transaction.get>>;
    }[] = [];
    if (patch.forCnc) {
      for (const release of patch.cncReleaseFrom ?? []) {
        if (!release.fileId || release.fileId === fileId || release.structureIds.length === 0) continue;
        const releaseRef = doc(db, COLLECTION, release.fileId);
        releaseReads.push({
          fileId: release.fileId,
          structureIds: release.structureIds,
          ref: releaseRef,
          snap: await transaction.get(releaseRef),
        });
      }
    }

    for (const release of releaseReads) {
      if (!release.snap.exists()) continue;
      const releaseData = release.snap.data() as DocumentData;
      const ids = Array.isArray(releaseData.cncStructureIds)
        ? (releaseData.cncStructureIds as unknown[]).map((id) => String(id ?? '')).filter(Boolean)
        : [];
      const names = Array.isArray(releaseData.cncStructureNames)
        ? (releaseData.cncStructureNames as unknown[]).map((name) => String(name ?? ''))
        : [];
      const nextClaim = claimAfterDroppingStructures(
        {
          fileId: release.fileId,
          fileName: String(releaseData.name || ''),
          structureIds: ids,
          structureNames: names,
        },
        release.structureIds,
      );
      transaction.update(release.ref, {
        forCnc: nextClaim.forCnc,
        cncStructureIds: nextClaim.structureIds,
        cncStructureNames: nextClaim.structureNames,
      });
    }

    const next: {
      versionLabel: string;
      detailStage?: ProjectFileDetailStage | ReturnType<typeof deleteField>;
      trueDimensions?: boolean | ReturnType<typeof deleteField>;
      forCnc?: boolean;
      cncStructureIds?: string[];
      cncStructureNames?: string[];
    } = { versionLabel: vt };
    if (patch.detailStage !== undefined) {
      const stage = parseProjectFileDetailStage(patch.detailStage);
      next.detailStage = stage ?? deleteField();
    }
    if (patch.trueDimensions === true) next.trueDimensions = true;
    else if (patch.trueDimensions === false) next.trueDimensions = deleteField();
    if (patch.forCnc === true) {
      next.forCnc = true;
      next.cncStructureIds = patch.cncStructureIds ?? [];
      next.cncStructureNames = patch.cncStructureNames ?? [];
    } else if (patch.forCnc === false) {
      next.forCnc = false;
      next.cncStructureIds = [];
      next.cncStructureNames = [];
    }
    transaction.update(fileRef, next);
  });
}

export async function listCncProjectFiles(projectId: string): Promise<ProjectFile[]> {
  const q = query(
    collection(db, COLLECTION),
    where('projectId', '==', projectId),
    where('forCnc', '==', true),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => fromFirestore({ id: d.id, data: () => d.data() }));
}

export async function uploadProjectFileVersion(
  projectId: string,
  streamId: string,
  file: File,
  uploadedBy: string,
  uploadedByName: string,
  versionLabel: string,
  options?: {
    versionNote?: string;
    dealId?: string;
    detailStage?: ProjectFileDetailStage;
    /** Timber CSV only. Keeps cross-section inches as written in the file. */
    trueDimensions?: boolean;
    forCnc?: boolean;
    cncStructureIds?: string[];
    cncStructureNames?: string[];
    /** Structures this upload takes from other CNC versions. Applied in the same write as the new file. */
    cncReleaseFrom?: { fileId: string; structureIds: string[] }[];
    /** This file is the one whole-project document for its type. */
    uploadedAsWholeProject?: boolean;
    /** Shared id for copies of one version placed on more than one group. */
    structureReleaseId?: string;
  },
): Promise<ProjectFile> {
  const streamRef = doc(db, STREAMS_COLLECTION, streamId);
  const streamSnap = await getDoc(streamRef);
  if (!streamSnap.exists()) throw new Error('Version track not found');
  const stream = streamFromFirestore({ id: streamSnap.id, data: () => streamSnap.data()! });
  if (stream.projectId !== projectId) throw new Error('This file history does not belong to this project');
  if (!fileMatchesStreamKind(file.type || '', file.name, stream.streamKind)) {
    throw new Error(
      'File type does not match this track (use 2D PDF, HTML, 3D model, or timber/hardware CSV as appropriate)',
    );
  }
  const vt = versionLabel.trim();
  if (!vt) throw new Error('Version label is required');
  const versionUploadBlock = projectFileUploadBlockReason(file);
  if (versionUploadBlock) throw new Error(versionUploadBlock);
  const detailStage = parseProjectFileDetailStage(options?.detailStage);
  if (!detailStage) throw new Error('Choose an LOD for this version');

  const mime = file.type || 'application/octet-stream';
  const kindTag = kindTagForTrackKind(stream.streamKind, file);
  const isTimberHead = kindTag === 'timber_list';
  const isHardwareHead = kindTag === 'hardware_list';

  const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const storagePath = `${STORAGE_PREFIX}/${projectId}/${Date.now()}-${safeName}`;
  const storageRef = ref(storage, storagePath);
  await ensureCrmStorageAccessBeforeUpload();
  const { payload, size: uploadSize } = await prepareTimberListUploadPayload(file, isTimberHead);
  await uploadBytes(storageRef, payload, { contentType: mime });
  const url = await getDownloadURL(storageRef);

  const newFileRef = doc(collection(db, COLLECTION));

  await runTransaction(db, async transaction => {
    const sSnap = await transaction.get(streamRef);
    if (!sSnap.exists()) throw new Error('Version track no longer exists');
    const sd = sSnap.data()!;
    if (sd.projectId !== projectId) throw new Error('Project mismatch for this track');

    const oldHeadId = sd.headFileId as string | undefined;
    const nextIndex = Number(sd.lastVersionIndex ?? 0) + 1;

    const releasedHeadIds = new Set<string>();
    const releaseReads: {
      fileId: string;
      structureIds: string[];
      ref: ReturnType<typeof doc>;
      snap: Awaited<ReturnType<typeof transaction.get>>;
    }[] = [];
    for (const release of options?.cncReleaseFrom ?? []) {
      if (!release.fileId || release.structureIds.length === 0) continue;
      const releaseRef = doc(db, COLLECTION, release.fileId);
      releaseReads.push({
        fileId: release.fileId,
        structureIds: release.structureIds,
        ref: releaseRef,
        snap: await transaction.get(releaseRef),
      });
    }

    for (const release of releaseReads) {
      if (!release.snap.exists()) continue;
      const releaseData = release.snap.data() as DocumentData;
      const ids = Array.isArray(releaseData.cncStructureIds)
        ? (releaseData.cncStructureIds as unknown[]).map((id) => String(id ?? '')).filter(Boolean)
        : [];
      const names = Array.isArray(releaseData.cncStructureNames)
        ? (releaseData.cncStructureNames as unknown[]).map((name) => String(name ?? ''))
        : [];
      const nextClaim = claimAfterDroppingStructures(
        {
          fileId: release.fileId,
          fileName: String(releaseData.name || ''),
          structureIds: ids,
          structureNames: names,
        },
        release.structureIds,
      );
      const patch: {
        forCnc: boolean;
        cncStructureIds: string[];
        cncStructureNames: string[];
        isHeadVersion?: boolean;
      } = {
        forCnc: nextClaim.forCnc,
        cncStructureIds: nextClaim.structureIds,
        cncStructureNames: nextClaim.structureNames,
      };
      if (release.fileId === oldHeadId) {
        patch.isHeadVersion = false;
        releasedHeadIds.add(release.fileId);
      }
      transaction.update(release.ref, patch);
    }

    if (oldHeadId && !releasedHeadIds.has(oldHeadId)) {
      transaction.update(doc(db, COLLECTION, oldHeadId), { isHeadVersion: false });
    }

    const cncFields = options?.forCnc
      ? {
          forCnc: true,
          cncStructureIds: options.cncStructureIds ?? [],
          cncStructureNames: options.cncStructureNames ?? [],
        }
      : {};

    transaction.set(newFileRef, {
      projectId,
      streamId,
      versionIndex: nextIndex,
      versionLabel: vt,
      versionNote: options?.versionNote?.trim() || '',
      detailStage,
      ...(options?.trueDimensions ? { trueDimensions: true } : {}),
      ...cncFields,
      name: file.name,
      url,
      storagePath,
      mimeType: mime,
      size: uploadSize,
      uploadedAt: Timestamp.now(),
      uploadedBy,
      uploadedByName,
      kindTag,
      isHeadVersion: true,
      isCurrentTimberList: isTimberHead,
      isCurrentHardwareList: isHardwareHead,
      ...(options?.uploadedAsWholeProject ? { uploadedAsWholeProject: true } : {}),
      ...(options?.structureReleaseId ? { structureReleaseId: options.structureReleaseId } : {}),
    });

    transaction.update(streamRef, {
      lastVersionIndex: nextIndex,
      headFileId: newFileRef.id,
      updatedAt: Timestamp.now(),
      lastSubmittedBy: uploadedBy,
      lastSubmittedByName: uploadedByName || '',
    });
  });

  if (isTimberHead) {
    await supersedePreviousTimberLists(projectId, newFileRef.id);
  }
  if (isHardwareHead) {
    await supersedePreviousHardwareLists(projectId, newFileRef.id);
  }

  const dealId = options?.dealId;
  if (isTimberHead && dealId) {
    const createdSnap = await getDoc(newFileRef);
    if (createdSnap.exists()) {
      const u = createdSnap.data()!.url as string;
      await syncProposalTimberListPrompt(dealId, newFileRef.id, file.name, u, uploadedByName, uploadedBy);
    }
  }
  if (isHardwareHead && dealId) {
    const createdSnap = await getDoc(newFileRef);
    if (createdSnap.exists()) {
      const u = createdSnap.data()!.url as string;
      await syncProposalHardwareListPrompt(dealId, newFileRef.id, file.name, u, uploadedByName, uploadedBy);
    }
  }

  const created = await getDoc(newFileRef);
  return fromFirestore({ id: created.id, data: () => created.data()! });
}

/** Register an existing Storage URL as a new version (e.g. task comment attachment). No duplicate bytes. */
export async function addLinkedVersionToStream(
  projectId: string,
  streamId: string,
  attachment: { name: string; url: string; type: string; size: number },
  uploadedBy: string,
  uploadedByName: string,
  versionLabel: string,
  versionNote?: string,
): Promise<ProjectFile> {
  const streamRef = doc(db, STREAMS_COLLECTION, streamId);
  const streamSnap = await getDoc(streamRef);
  if (!streamSnap.exists()) throw new Error('Version track not found');
  const stream = streamFromFirestore({ id: streamSnap.id, data: () => streamSnap.data()! });
  if (stream.projectId !== projectId) throw new Error('This file history does not belong to this project');
  if (!fileMatchesStreamKind(attachment.type || '', attachment.name, stream.streamKind)) {
    throw new Error('Attachment type does not match this track');
  }
  const vt = versionLabel.trim();
  if (!vt) throw new Error('Version label is required');
  const linkedUploadBlock = projectFileUploadBlockReason({ name: attachment.name, size: attachment.size });
  if (linkedUploadBlock) throw new Error(linkedUploadBlock);

  const mime = attachment.type || 'application/octet-stream';
  const kindTag = kindTagForTrackKind(stream.streamKind, { name: attachment.name, type: mime });
  const isTimberHead = kindTag === 'timber_list';
  const isHardwareHead = kindTag === 'hardware_list';

  const newFileRef = doc(collection(db, COLLECTION));

  await runTransaction(db, async transaction => {
    const sSnap = await transaction.get(streamRef);
    if (!sSnap.exists()) throw new Error('Version track no longer exists');
    const sd = sSnap.data()!;
    if (sd.projectId !== projectId) throw new Error('Project mismatch for this track');

    const oldHeadId = sd.headFileId as string | undefined;
    const nextIndex = Number(sd.lastVersionIndex ?? 0) + 1;

    if (oldHeadId) {
      transaction.update(doc(db, COLLECTION, oldHeadId), { isHeadVersion: false });
    }

    transaction.set(newFileRef, {
      projectId,
      streamId,
      versionIndex: nextIndex,
      versionLabel: vt,
      versionNote: (versionNote || '').trim(),
      name: attachment.name,
      url: attachment.url,
      storagePath: '',
      mimeType: mime,
      size: attachment.size || 0,
      uploadedAt: Timestamp.now(),
      uploadedBy,
      uploadedByName,
      kindTag,
      isHeadVersion: true,
      isCurrentTimberList: isTimberHead,
      isCurrentHardwareList: isHardwareHead,
    });

    transaction.update(streamRef, {
      lastVersionIndex: nextIndex,
      headFileId: newFileRef.id,
      updatedAt: Timestamp.now(),
      lastSubmittedBy: uploadedBy,
      lastSubmittedByName: uploadedByName || '',
    });
  });

  if (isTimberHead) {
    await supersedePreviousTimberLists(projectId, newFileRef.id);
  }
  if (isHardwareHead) {
    await supersedePreviousHardwareLists(projectId, newFileRef.id);
  }

  const created = await getDoc(newFileRef);
  return fromFirestore({ id: created.id, data: () => created.data()! });
}

export function subscribeProjectFileStreams(
  projectId: string,
  callback: (streams: ProjectFileStream[]) => void,
  options?: { streamKinds?: readonly ProjectFileStreamKind[] },
): () => void {
  const kinds = options?.streamKinds?.filter(Boolean) ?? [];
  const q = kinds.length
    ? query(
      collection(db, STREAMS_COLLECTION),
      where('projectId', '==', projectId),
      where('streamKind', 'in', [...kinds]),
      orderBy('updatedAt', 'desc'),
    )
    : query(
      collection(db, STREAMS_COLLECTION),
      where('projectId', '==', projectId),
      orderBy('updatedAt', 'desc'),
    );
  return onSnapshot(
    q,
    snap => {
      callback(snap.docs.map(d => streamFromFirestore({ id: d.id, data: () => d.data() })));
    },
    err => {
      console.error('projectFileStreams listen failed', err);
      callback([]);
    },
  );
}

/**
 * Versions for one stream (newest first). Pass `rootStreamId` for a structure fork
 * (`stream.forkedFromStreamId`) to merge in the pre-split history it inherits.
 */
export function subscribeProjectFileVersionsForStream(
  projectId: string,
  streamId: string,
  callback: (files: ProjectFile[]) => void,
  rootStreamId?: string | null,
  /** When set, root files uploaded earlier than this are left off the track. */
  inheritVersionsAfter?: Date | null,
): () => void {
  const versionsQuery = (sid: string) =>
    query(
      collection(db, COLLECTION),
      where('projectId', '==', projectId),
      where('streamId', '==', sid),
      orderBy('versionIndex', 'desc'),
    );
  const toFiles = (snap: { docs: Array<{ id: string; data: () => DocumentData }> }) =>
    snap.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }));

  if (!rootStreamId || rootStreamId === streamId) {
    return onSnapshot(
      versionsQuery(streamId),
      snap => callback(toFiles(snap)),
      err => {
        console.error('projectFile versions listen failed', err);
        callback([]);
      },
    );
  }

  let own: ProjectFile[] | null = null;
  let inherited: ProjectFile[] | null = null;
  const emit = () => {
    if (own === null || inherited === null) return;
    const cutoff = inheritVersionsAfter instanceof Date ? inheritVersionsAfter.getTime() : null;
    const visible = cutoff == null
      ? inherited
      : inherited.filter(f => (f.uploadedAt instanceof Date ? f.uploadedAt.getTime() : 0) >= cutoff);
    callback(mergeStreamVersions(own, visible));
  };
  const onErr = (err: unknown) => {
    console.error('projectFile versions listen failed', err);
    own = own ?? [];
    inherited = inherited ?? [];
    emit();
  };
  const unsubOwn = onSnapshot(versionsQuery(streamId), snap => { own = toFiles(snap); emit(); }, onErr);
  const unsubRoot = onSnapshot(versionsQuery(rootStreamId), snap => { inherited = toFiles(snap); emit(); }, onErr);
  return () => {
    unsubOwn();
    unsubRoot();
  };
}

/**
 * Recompute a track's head / `lastVersionIndex` / `isHeadVersion` flags from the
 * versions that currently point at it. Shared by delete, move and fork flows.
 */
export async function reconcileStreamHead(projectId: string, streamId: string): Promise<void> {
  const q = query(
    collection(db, COLLECTION),
    where('projectId', '==', projectId),
    where('streamId', '==', streamId),
    orderBy('versionIndex', 'desc'),
  );
  const streamRef = doc(db, STREAMS_COLLECTION, streamId);
  const [snap, streamSnap] = await Promise.all([getDocs(q), getDoc(streamRef)]);
  const streamData = streamSnap.exists() ? streamSnap.data() : undefined;
  const rootId = typeof streamData?.forkedFromStreamId === 'string' ? streamData.forkedFromStreamId : '';

  if (snap.empty) {
    // A structure fork with no uploads of its own still shows the root's history —
    // keep its head / numbering pointed at the root rather than clearing them.
    // A track added after the split does not: its timeline starts at inheritVersionsAfter.
    if (rootId && !streamData?.inheritVersionsAfter) {
      const rootSnap = await getDoc(doc(db, STREAMS_COLLECTION, rootId));
      const rd = rootSnap.exists() ? rootSnap.data() : undefined;
      if (rd) {
        await updateDoc(streamRef, {
          lastVersionIndex: Number(rd.lastVersionIndex ?? 0),
          headFileId: rd.headFileId ?? deleteField(),
          updatedAt: Timestamp.now(),
          lastSubmittedBy: rd.lastSubmittedBy ?? deleteField(),
          lastSubmittedByName: rd.lastSubmittedByName ?? deleteField(),
        });
        return;
      }
    }
    await updateDoc(streamRef, {
      lastVersionIndex: 0,
      headFileId: deleteField(),
      updatedAt: Timestamp.now(),
      lastSubmittedBy: deleteField(),
      lastSubmittedByName: deleteField(),
    });
    return;
  }

  // Upload date picks the head. Version numbers only break a tie, so moving an
  // older file (which appends a higher index) does not put it on top.
  let head = snap.docs[0];
  let headAt = -1;
  let maxIndex = 0;
  for (const item of snap.docs) {
    const data = item.data();
    const index = Number(data.versionIndex ?? 0);
    if (index > maxIndex) maxIndex = index;
    const uploaded = typeof data.uploadedAt?.toMillis === 'function' ? data.uploadedAt.toMillis() : 0;
    const headIndex = Number(head.data().versionIndex ?? 0);
    if (uploaded > headAt || (uploaded === headAt && index >= headIndex)) {
      head = item;
      headAt = uploaded;
    }
  }
  const updates: Promise<unknown>[] = [];
  snap.docs.forEach(d => {
    const shouldHead = d.id === head.id;
    if ((d.data().isHeadVersion === true) !== shouldHead) {
      updates.push(updateDoc(d.ref, { isHeadVersion: shouldHead }));
    }
  });
  const headData = head.data();
  updates.push(
    updateDoc(streamRef, {
      lastVersionIndex: maxIndex,
      headFileId: head.id,
      updatedAt: Timestamp.now(),
      lastSubmittedBy: (headData.uploadedBy as string) || '',
      lastSubmittedByName: (headData.uploadedByName as string) || '',
    }),
  );
  await Promise.all(updates);
}

const reconcileStreamHeadAfterVersionDelete = reconcileStreamHead;

export type MoveVersionsTarget =
  | { streamId: string }
  | { newTrack: { title: string; structures: { id: string; name: string }[] } };

export interface MoveVersionsResult {
  targetStreamId: string;
  movedFileIds: string[];
  createdStream: boolean;
}

/**
 * Move past versions out of one track into another (existing or new) so they
 * live on the right structure stream. Versions keep their label / note / LOD /
 * upload date and gain a `moveHistory` hop; both tracks get their head recomputed.
 * Portal / RFQ release docs keep their frozen `sourceStreamId` — readers resolve the
 * *current* track from `projectFiles.streamId` so history stays truthful.
 */
export async function moveProjectFileVersionsToStream(
  projectId: string,
  fileIds: string[],
  target: MoveVersionsTarget,
  actor: { uid: string; name: string },
  options?: { allowFromSharedRoot?: boolean; allowToSharedRoot?: boolean },
): Promise<MoveVersionsResult> {
  const ids = Array.from(new Set(fileIds.map(id => (id || '').trim()).filter(Boolean)));
  if (ids.length === 0) throw new Error('Pick at least one version to move.');

  const fileSnaps = await Promise.all(ids.map(id => getDoc(doc(db, COLLECTION, id))));
  const files = fileSnaps.map((s, i) => {
    if (!s.exists()) throw new Error('One of the selected versions no longer exists.');
    const d = s.data()!;
    if (d.projectId !== projectId) throw new Error('A selected version does not belong to this project.');
    if (!d.streamId) throw new Error('Only versioned files can be moved between tracks.');
    return { id: ids[i], data: d };
  });
  const sourceStreamIds = Array.from(new Set(files.map(f => String(f.data.streamId))));
  if (sourceStreamIds.length !== 1) throw new Error('Select versions from a single track.');
  const sourceStreamId = sourceStreamIds[0];
  const sourceSnap = await getDoc(doc(db, STREAMS_COLLECTION, sourceStreamId));
  if (!sourceSnap.exists()) throw new Error('Source track not found.');
  const source = streamFromFirestore({ id: sourceSnap.id, data: () => sourceSnap.data()! });
  if (source.projectId !== projectId) throw new Error('Source track does not belong to this project.');
  if (source.wholeProjectRoot && !options?.allowFromSharedRoot) {
    throw new Error('Pre-split versions are shared by every structure track and cannot be moved.');
  }

  let targetStreamId: string;
  let createdStream = false;
  if ('streamId' in target) {
    targetStreamId = (target.streamId || '').trim();
    if (!targetStreamId) throw new Error('Choose a destination track.');
    if (targetStreamId === sourceStreamId) throw new Error('Those versions are already on that track.');
    const tSnap = await getDoc(doc(db, STREAMS_COLLECTION, targetStreamId));
    if (!tSnap.exists()) throw new Error('Destination track not found.');
    const tgt = streamFromFirestore({ id: tSnap.id, data: () => tSnap.data()! });
    if (tgt.projectId !== projectId) throw new Error('Destination track does not belong to this project.');
    if (tgt.streamKind !== source.streamKind) {
      throw new Error('Versions can only move between tracks of the same type.');
    }
    if (tgt.wholeProjectRoot && !options?.allowToSharedRoot) {
      throw new Error('The whole-project root is read-only after the split.');
    }
  } else {
    const title = target.newTrack.title.trim();
    if (!title) throw new Error('Name the new track.');
    const created = await createProjectFileStream(projectId, title, source.streamKind, actor.uid, undefined, {
      structures: target.newTrack.structures,
      streamRole: source.streamRole,
      portalDisplayName: source.portalDisplayName,
    });
    targetStreamId = created.id!;
    createdStream = true;
  }

  const ordered = [...files].sort((a, b) => {
    const at = (a.data.uploadedAt as { toMillis?: () => number })?.toMillis?.() ?? 0;
    const bt = (b.data.uploadedAt as { toMillis?: () => number })?.toMillis?.() ?? 0;
    if (at !== bt) return at - bt;
    return Number(a.data.versionIndex ?? 0) - Number(b.data.versionIndex ?? 0);
  });

  const targetRef = doc(db, STREAMS_COLLECTION, targetStreamId);
  await runTransaction(db, async tx => {
    const tSnap = await tx.get(targetRef);
    if (!tSnap.exists()) throw new Error('Destination track no longer exists.');
    let nextIndex = Number(tSnap.data()!.lastVersionIndex ?? 0);
    const now = Timestamp.now();
    for (const f of ordered) {
      nextIndex += 1;
      const hop: Record<string, unknown> = {
        fromStreamId: sourceStreamId,
        fromStreamTitle: source.title,
        fromVersionIndex: Number(f.data.versionIndex ?? 0),
        toStreamId: targetStreamId,
        toVersionIndex: nextIndex,
        movedBy: actor.uid,
        movedByName: actor.name || '',
        movedAt: now,
      };
      const history = Array.isArray(f.data.moveHistory) ? [...(f.data.moveHistory as unknown[]), hop] : [hop];
      tx.update(doc(db, COLLECTION, f.id), {
        streamId: targetStreamId,
        versionIndex: nextIndex,
        isHeadVersion: false,
        moveHistory: history,
        sharedOnStreamIds: deleteField(),
      });
    }
    tx.update(targetRef, { lastVersionIndex: nextIndex, updatedAt: now });
  });

  // Plan-review sessions follow the version they were started on.
  try {
    const reviews = await getDocs(
      query(collection(db, 'planReviews'), where('projectId', '==', projectId), where('streamId', '==', sourceStreamId)),
    );
    const movedSet = new Set(ids);
    await Promise.all(
      reviews.docs
        .filter(d => movedSet.has(String(d.data().documentId || '')))
        .map(d => updateDoc(d.ref, { streamId: targetStreamId })),
    );
  } catch (err) {
    console.warn('[projectFileService] planReviews stream re-point failed', err);
  }

  await Promise.all([reconcileStreamHead(projectId, sourceStreamId), reconcileStreamHead(projectId, targetStreamId)]);
  return { targetStreamId, movedFileIds: ordered.map(f => f.id), createdStream };
}

/**
 * The one whole-project track for a file type. Created on demand, and any
 * same-kind track that does not already inherit a parent is linked to it so
 * the shared file shows up on every structure group.
 */
async function ensureWholeProjectRoot(
  projectId: string,
  streamKind: ProjectFileStreamKind,
  knownStreams: ProjectFileStream[],
  actorUid: string,
): Promise<{ root: ProjectFileStream; streams: ProjectFileStream[] }> {
  const streams = knownStreams.map(stream => ({ ...stream }));
  let root = streams.find(stream => stream.id && stream.wholeProjectRoot && stream.streamKind === streamKind);
  if (!root?.id) {
    const created = await createProjectFileStream(
      projectId,
      structureTrackKindLabel(streamKind),
      streamKind,
      actorUid,
    );
    if (!created.id) throw new Error('Could not create the shared whole-project track.');
    await updateDoc(doc(db, STREAMS_COLLECTION, created.id), { wholeProjectRoot: true });
    root = { ...created, wholeProjectRoot: true };
    streams.push(root);
  }
  const rootId = root.id;
  const orphans = streams.filter(
    stream =>
      !!stream.id &&
      stream.id !== rootId &&
      stream.streamKind === streamKind &&
      !stream.wholeProjectRoot &&
      !stream.forkedFromStreamId,
  );
  if (orphans.length > 0) {
    const now = Timestamp.now();
    await Promise.all(
      orphans.map(stream =>
        updateDoc(doc(db, STREAMS_COLLECTION, stream.id!), {
          forkedFromStreamId: rootId,
          updatedAt: now,
        }),
      ),
    );
    for (const orphan of orphans) orphan.forkedFromStreamId = rootId;
  }
  return { root, streams };
}

/**
 * Upload one file onto the shared whole-project track for its type. Every
 * structure group that inherits that track opens this same document.
 */
export async function uploadSharedWholeProjectVersion(
  projectId: string,
  file: File,
  uploadedBy: string,
  uploadedByName: string,
  versionLabel: string,
  streamKind: ProjectFileStreamKind,
  knownStreams: ProjectFileStream[],
  options?: {
    versionNote?: string;
    dealId?: string;
    detailStage?: ProjectFileDetailStage;
    trueDimensions?: boolean;
    forCnc?: boolean;
    cncStructureIds?: string[];
    cncStructureNames?: string[];
    cncReleaseFrom?: { fileId: string; structureIds: string[] }[];
  },
): Promise<{ file: ProjectFile; streams: ProjectFileStream[] }> {
  const { root, streams } = await ensureWholeProjectRoot(projectId, streamKind, knownStreams, uploadedBy);
  if (!root.id) throw new Error('Could not open the shared whole-project track.');
  const uploaded = await uploadProjectFileVersion(
    projectId,
    root.id,
    file,
    uploadedBy,
    uploadedByName,
    versionLabel,
    { ...options, uploadedAsWholeProject: true },
  );
  return { file: uploaded, streams };
}

/**
 * Place one version on the selected structure groups' tracks of the same file
 * type. Two or more groups share that one document. Whole project keeps it on
 * the shared root, so every structure track opens that same document. Groups
 * that are no longer selected lose it.
 */
export async function assignProjectFileToStructureGroups(
  projectId: string,
  fileId: string,
  groups: { structureIds: string[]; structureNames: string[]; label: string }[],
  knownStreams: ProjectFileStream[],
  knownFiles: ProjectFile[],
  actor: { uid: string; name: string },
  options?: { markWholeProject?: boolean },
): Promise<void> {
  const selected = groups.filter(group => group.structureIds.length > 0);
  if (selected.length === 0) throw new Error('Choose at least one structure group.');

  const fileSnap = await getDoc(doc(db, COLLECTION, fileId));
  if (!fileSnap.exists()) throw new Error('That version no longer exists.');
  const fileData = fileSnap.data()!;
  if (fileData.projectId !== projectId) throw new Error('That version does not belong to this project.');
  const sourceStreamId = String(fileData.streamId || '');
  if (!sourceStreamId) throw new Error('Only versioned files can move between structure groups.');
  const sourceSnap = await getDoc(doc(db, STREAMS_COLLECTION, sourceStreamId));
  if (!sourceSnap.exists()) throw new Error('Source track not found.');
  const source = streamFromFirestore({ id: sourceSnap.id, data: () => sourceSnap.data()! });
  if (source.projectId !== projectId) throw new Error('Source track does not belong to this project.');

  const loaded = fromFirestore({ id: fileSnap.id, data: () => fileData });
  const files = knownFiles.some(item => item.id === fileId) ? knownFiles : [loaded, ...knownFiles];
  const sameKindIds = new Set(
    knownStreams.filter(stream => stream.id && stream.streamKind === source.streamKind).map(stream => stream.id!),
  );
  const family = filesInSameStructureRelease(loaded, files, sameKindIds);
  if (options?.markWholeProject) {
    const { root } = await ensureWholeProjectRoot(projectId, source.streamKind, knownStreams, actor.uid);
    if (!root.id) throw new Error('Could not open the shared whole-project track.');
    if (sourceStreamId !== root.id) {
      await moveProjectFileVersionsToStream(
        projectId,
        [fileId],
        { streamId: root.id },
        actor,
        { allowToSharedRoot: true, allowFromSharedRoot: source.wholeProjectRoot === true },
      );
    }
    await updateDoc(doc(db, COLLECTION, fileId), {
      uploadedAsWholeProject: true,
      structureReleaseId: deleteField(),
      sharedOnStreamIds: deleteField(),
    });
    for (const extra of family) {
      if (!extra.id || extra.id === fileId) continue;
      await deleteProjectFileCopy(projectId, extra.id);
    }
    await followPortalReleaseScope(fileId, root);
    return;
  }

  let tracks = [...knownStreams];
  const destinationIds: string[] = [];
  for (const group of selected) {
    const wantedKey = structureSetKey(group.structureIds);
    const existing = tracks.find(
      stream =>
        !!stream.id &&
        stream.streamKind === source.streamKind &&
        !stream.wholeProjectRoot &&
        structureSetKey(stream.structureIds ?? []) === wantedKey,
    );
    if (existing?.id) {
      if (!destinationIds.includes(existing.id)) destinationIds.push(existing.id);
      continue;
    }
    const structures = group.structureIds.map((id, index) => ({
      id,
      name: group.structureNames[index] || id,
    }));
    const title = `${group.label.trim() || structureSelectionLabel(structures.map(item => item.name), { maxNamed: 3 })} — ${structureTrackKindLabel(source.streamKind)}`;
    const created = await createProjectFileStream(projectId, title, source.streamKind, actor.uid, undefined, {
      structures,
    });
    if (!created.id) throw new Error('Could not create a track for that structure group.');
    tracks = [...tracks, created];
    destinationIds.push(created.id);
  }

  if (destinationIds.length >= 2) {
    const onDestination = family.filter(
      item => item.id && item.streamId && destinationIds.includes(item.streamId),
    );
    const canonical =
      onDestination.find(item => item.id === fileId) ||
      [...onDestination].sort((a, b) => (a.versionIndex ?? 0) - (b.versionIndex ?? 0))[0] ||
      family.find(item => item.id === fileId) ||
      family.find(item => item.id);
    if (!canonical?.id) throw new Error('That version no longer exists.');
    const homeId = destinationIds.includes(canonical.streamId || '')
      ? canonical.streamId!
      : destinationIds[0];
    if (canonical.streamId !== homeId) {
      const donorStream = tracks.find(stream => stream.id === canonical.streamId);
      await moveProjectFileVersionsToStream(
        projectId,
        [canonical.id],
        { streamId: homeId },
        actor,
        { allowFromSharedRoot: donorStream?.wholeProjectRoot === true || source.wholeProjectRoot === true },
      );
    }
    const sharedOnStreamIds = destinationIds.filter(id => id !== homeId);
    await updateDoc(doc(db, COLLECTION, canonical.id), {
      sharedOnStreamIds,
      uploadedAsWholeProject: deleteField(),
      structureReleaseId: deleteField(),
      ...(family.some(item => item.isCurrentTimberList) ? { isCurrentTimberList: true } : {}),
      ...(family.some(item => item.isCurrentHardwareList) ? { isCurrentHardwareList: true } : {}),
    });
    for (const extra of family) {
      if (!extra.id || extra.id === canonical.id) continue;
      await deleteProjectFileCopy(projectId, extra.id);
    }
    const home = tracks.find(stream => stream.id === homeId);
    if (home && canonical.id) await followPortalReleaseScope(canonical.id, home);
    return;
  }

  const releaseId =
    (typeof fileData.structureReleaseId === 'string' && fileData.structureReleaseId.trim()) ||
    family.find(item => item.structureReleaseId)?.structureReleaseId ||
    doc(collection(db, COLLECTION)).id;
  const markWholeProject = options?.markWholeProject === true;
  const consumed = new Set<string>();
  const keptIds: string[] = [];

  for (const destinationId of destinationIds) {
    const already = family.find(item => item.id && item.streamId === destinationId && !consumed.has(item.id));
    if (already?.id) {
      consumed.add(already.id);
      keptIds.push(already.id);
      continue;
    }
    const donor = family.find(item => item.id && !consumed.has(item.id));
    if (donor?.id) {
      consumed.add(donor.id);
      if (donor.streamId && donor.streamId !== destinationId) {
        const donorStream = tracks.find(stream => stream.id === donor.streamId);
        await moveProjectFileVersionsToStream(
          projectId,
          [donor.id],
          { streamId: destinationId },
          actor,
          { allowFromSharedRoot: donorStream?.wholeProjectRoot === true },
        );
      }
      keptIds.push(donor.id);
      continue;
    }
    const copyId = await copyProjectFileOntoStream(projectId, fileId, destinationId, {
      structureReleaseId: releaseId,
      uploadedAsWholeProject: markWholeProject,
      actor,
    });
    keptIds.push(copyId);
  }

  await Promise.all(
    keptIds.map(id =>
      updateDoc(doc(db, COLLECTION, id), {
        structureReleaseId: releaseId,
        sharedOnStreamIds: deleteField(),
        ...(markWholeProject ? { uploadedAsWholeProject: true } : { uploadedAsWholeProject: deleteField() }),
      }),
    ),
  );

  const extras = Array.from(
    new Set(family.map(item => item.id).filter((id): id is string => !!id && !keptIds.includes(id))),
  );
  for (const extraId of extras) {
    await deleteProjectFileCopy(projectId, extraId);
  }
  const home = tracks.find(stream => stream.id === destinationIds[0]);
  if (home) {
    for (const id of Array.from(new Set([fileId, ...keptIds]))) await followPortalReleaseScope(id, home);
  }
}

/** Copy one version onto another track without uploading the bytes again. */
async function copyProjectFileOntoStream(
  projectId: string,
  sourceFileId: string,
  targetStreamId: string,
  options: { structureReleaseId: string; uploadedAsWholeProject: boolean; actor: { uid: string; name: string } },
): Promise<string> {
  const sourceSnap = await getDoc(doc(db, COLLECTION, sourceFileId));
  if (!sourceSnap.exists()) throw new Error('That version no longer exists.');
  const sourceData = sourceSnap.data()!;
  if (sourceData.projectId !== projectId) throw new Error('That version does not belong to this project.');
  const sourceStreamId = String(sourceData.streamId || '');
  let fromStreamTitle = '';
  if (sourceStreamId) {
    const sourceStreamSnap = await getDoc(doc(db, STREAMS_COLLECTION, sourceStreamId));
    if (sourceStreamSnap.exists()) fromStreamTitle = String(sourceStreamSnap.data()!.title || '');
  }
  const targetRef = doc(db, STREAMS_COLLECTION, targetStreamId);
  const targetSnap = await getDoc(targetRef);
  if (!targetSnap.exists()) throw new Error('Destination track not found.');
  const target = targetSnap.data()!;
  if (target.projectId !== projectId) throw new Error('Destination track does not belong to this project.');
  const destStructureIds = new Set(
    (Array.isArray(target.structureIds) ? (target.structureIds as unknown[]) : [])
      .map(id => String(id || ''))
      .filter(Boolean),
  );
  const sourceCncIds = Array.isArray(sourceData.cncStructureIds)
    ? (sourceData.cncStructureIds as unknown[]).map(id => String(id || '')).filter(Boolean)
    : [];
  const sourceCncNames = Array.isArray(sourceData.cncStructureNames)
    ? (sourceData.cncStructureNames as unknown[]).map(name => String(name || ''))
    : [];
  const cncIds = sourceCncIds.filter(id => destStructureIds.has(id));
  const cncNames = cncIds.map(id => sourceCncNames[sourceCncIds.indexOf(id)] || id);
  const newRef = doc(collection(db, COLLECTION));

  await runTransaction(db, async transaction => {
    const freshTarget = await transaction.get(targetRef);
    if (!freshTarget.exists()) throw new Error('Destination track no longer exists.');
    const track = freshTarget.data()!;
    const nextIndex = Number(track.lastVersionIndex ?? 0) + 1;
    const payload: Record<string, unknown> = {
      projectId,
      streamId: targetStreamId,
      versionIndex: nextIndex,
      versionLabel: String(sourceData.versionLabel || 'Version'),
      versionNote: String(sourceData.versionNote || ''),
      name: sourceData.name,
      url: sourceData.url,
      storagePath: sourceData.storagePath || '',
      mimeType: sourceData.mimeType || 'application/octet-stream',
      size: Number(sourceData.size || 0),
      uploadedAt: sourceData.uploadedAt || Timestamp.now(),
      uploadedBy: sourceData.uploadedBy,
      uploadedByName: sourceData.uploadedByName || '',
      kindTag: sourceData.kindTag,
      isHeadVersion: false,
      isCurrentTimberList: sourceData.isCurrentTimberList === true,
      isCurrentHardwareList: sourceData.isCurrentHardwareList === true,
      structureReleaseId: options.structureReleaseId,
    };
    if (sourceData.detailStage) payload.detailStage = sourceData.detailStage;
    if (sourceData.trueDimensions === true) payload.trueDimensions = true;
    if (sourceData.forCnc === true && cncIds.length > 0) {
      payload.forCnc = true;
      payload.cncStructureIds = cncIds;
      payload.cncStructureNames = cncNames;
    }
    if (options.uploadedAsWholeProject) payload.uploadedAsWholeProject = true;
    // A copy is not an upload. moveHistory tells the version-created trigger to stay quiet
    // instead of announcing the original uploader again.
    payload.moveHistory = [{
      fromStreamId: sourceStreamId,
      fromStreamTitle,
      fromVersionIndex: Number(sourceData.versionIndex ?? 0),
      toStreamId: targetStreamId,
      toVersionIndex: nextIndex,
      movedBy: options.actor.uid,
      movedByName: options.actor.name || '',
      movedAt: Timestamp.now(),
    }];
    if (Array.isArray(sourceData.htmlModelDescriptors)) payload.htmlModelDescriptors = sourceData.htmlModelDescriptors;
    if (typeof sourceData.htmlPreviewImageUrl === 'string' && sourceData.htmlPreviewImageUrl.trim()) {
      payload.htmlPreviewImageUrl = sourceData.htmlPreviewImageUrl.trim();
    }
    transaction.set(newRef, payload);
    transaction.update(targetRef, {
      lastVersionIndex: nextIndex,
      updatedAt: Timestamp.now(),
    });
  });
  await reconcileStreamHead(projectId, targetStreamId);
  return newRef.id;
}

/** Drop one copy of a version. Shared storage stays until the last copy is gone. */
async function deleteProjectFileCopy(projectId: string, fileId: string): Promise<void> {
  const snap = await getDoc(doc(db, COLLECTION, fileId));
  if (!snap.exists()) return;
  const data = snap.data()!;
  if (data.projectId !== projectId) return;
  const streamId = typeof data.streamId === 'string' ? data.streamId : '';
  const storagePath = typeof data.storagePath === 'string' ? data.storagePath : '';
  let shared = true;
  if (storagePath) {
    try {
      const peers = await getDocs(query(collection(db, COLLECTION), where('storagePath', '==', storagePath)));
      shared = peers.docs.some(peer => peer.id !== fileId);
    } catch (err) {
      console.warn('[projectFileService] storage-share check failed', err);
      shared = true;
    }
  }
  await deleteDoc(doc(db, COLLECTION, fileId));
  if (!shared && storagePath) {
    try {
      await deleteObject(ref(storage, storagePath));
    } catch {
      // The bytes may already be gone.
    }
  }
  if (streamId) await reconcileStreamHead(projectId, streamId);
}

export interface ForkStreamTarget {
  /** Editable title; defaults to "{Structures} — {original title}". */
  title?: string;
  structures: { id: string; name: string }[];
}

export interface ForkStreamResult {
  rootStreamId: string;
  forkedStreamIds: string[];
}

/**
 * Split at unlock: the whole-project track becomes the shared root and every
 * target (a *set* of structures) gets its own track that **inherits** the root's
 * versions by reference (`forkedFromStreamId`) — no `projectFiles` rows are
 * copied. Each fork opens with the root's head and `lastVersionIndex`, so its own
 * uploads continue the numbering (#13… after a 12-version root). The root is
 * marked `wholeProjectRoot` and locked for new uploads.
 */
export async function forkStreamIntoStructures(
  projectId: string,
  streamId: string,
  targets: ForkStreamTarget[],
  actor: { uid: string; name: string },
): Promise<ForkStreamResult> {
  const cleanTargets = targets
    .map(t => ({ title: (t.title || '').trim(), structures: normalizeStructureRefs(t.structures) }))
    .filter(t => t.structures.length > 0);
  if (cleanTargets.length === 0) throw new Error('Pick at least one structure to split into.');
  const claimed = new Set<string>();
  for (const t of cleanTargets) {
    for (const s of t.structures) {
      if (claimed.has(s.id)) throw new Error(`"${s.name}" is in more than one target track.`);
      claimed.add(s.id);
    }
  }

  const streamRef = doc(db, STREAMS_COLLECTION, streamId);
  const streamSnap = await getDoc(streamRef);
  if (!streamSnap.exists()) throw new Error('Version track not found');
  const root = streamFromFirestore({ id: streamSnap.id, data: () => streamSnap.data()! });
  if (root.projectId !== projectId) throw new Error('This version history does not belong to the project');
  if ((root.structureIds ?? []).length > 0) throw new Error('Only untagged (whole-project) tracks can be split.');
  if (root.wholeProjectRoot) throw new Error('This track has already been split.');

  const inherited: DocumentData = {
    forkedFromStreamId: streamId,
    lastVersionIndex: root.lastVersionIndex ?? 0,
    updatedAt: Timestamp.now(),
  };
  if (root.headFileId) inherited.headFileId = root.headFileId;
  if (root.lastSubmittedBy) inherited.lastSubmittedBy = root.lastSubmittedBy;
  if (root.lastSubmittedByName) inherited.lastSubmittedByName = root.lastSubmittedByName;

  const forkedStreamIds: string[] = [];
  for (const t of cleanTargets) {
    const names = t.structures.map(s => s.name).filter(Boolean);
    const autoTitle = `${names.length <= 2 ? names.join(' + ') : `${names.slice(0, 2).join(' + ')} +${names.length - 2}`} — ${root.title}`;
    const created = await createProjectFileStream(projectId, t.title || autoTitle, root.streamKind, actor.uid, root.description, {
      structures: t.structures,
      streamRole: root.streamRole,
      portalDisplayName: root.portalDisplayName,
    });
    const forkId = created.id!;
    forkedStreamIds.push(forkId);
    // Inherit, don't copy: the fork points at the root and opens with its head / numbering.
    await updateDoc(doc(db, STREAMS_COLLECTION, forkId), inherited);
  }

  await updateDoc(streamRef, {
    wholeProjectRoot: true,
    forkedToStreamIds: forkedStreamIds,
    updatedAt: Timestamp.now(),
  });

  return { rootStreamId: streamId, forkedStreamIds };
}

export interface AddForkToRootTarget {
  /** Defaults to "{Structures} — {root title}". */
  title?: string;
  structures: { id: string; name: string }[];
  /** Files-rail heading, when this fork starts a named group. */
  structureGroupLabel?: string;
}

/**
 * After a track has already been split, add one more structure track.
 * It points at the root so later whole-project uploads show up, but files
 * already on the root stay off this track (`inheritVersionsAfter`).
 * The new id is appended to `forkedToStreamIds`; the root stays locked.
 */
export async function addForkToWholeProjectRoot(
  projectId: string,
  rootStreamId: string,
  target: AddForkToRootTarget,
  actor: { uid: string; name: string },
): Promise<{ rootStreamId: string; forkedStreamId: string }> {
  const structures = normalizeStructureRefs(target.structures);
  if (structures.length === 0) throw new Error('Pick at least one structure for the new track.');

  const streamRef = doc(db, STREAMS_COLLECTION, rootStreamId);
  const streamSnap = await getDoc(streamRef);
  if (!streamSnap.exists()) throw new Error('Version track not found');
  const root = streamFromFirestore({ id: streamSnap.id, data: () => streamSnap.data()! });
  if (root.projectId !== projectId) throw new Error('This version history does not belong to the project');
  if (!root.wholeProjectRoot) throw new Error('Only a whole-project root can gain another structure track.');

  const names = structures.map(s => s.name).filter(Boolean);
  const autoTitle = `${names.length <= 2 ? names.join(' + ') : `${names.slice(0, 2).join(' + ')} +${names.length - 2}`} — ${root.title}`;
  const groupLabel = (target.structureGroupLabel || '').trim();
  const created = await createProjectFileStream(
    projectId,
    (target.title || '').trim() || autoTitle,
    root.streamKind,
    actor.uid,
    root.description,
    {
      structures,
      streamRole: root.streamRole,
      portalDisplayName: root.portalDisplayName,
      ...(groupLabel ? { structureGroupLabel: groupLabel } : {}),
    },
  );
  const forkId = created.id!;
  const addedAt = Timestamp.now();
  await updateDoc(doc(db, STREAMS_COLLECTION, forkId), {
    forkedFromStreamId: rootStreamId,
    inheritVersionsAfter: addedAt,
    lastVersionIndex: 0,
    updatedAt: addedAt,
  });

  await updateDoc(streamRef, {
    forkedToStreamIds: [...(root.forkedToStreamIds ?? []), forkId],
    updatedAt: Timestamp.now(),
  });

  return { rootStreamId, forkedStreamId: forkId };
}

/** Record the instant a late-added structure track starts its timeline. No-op once set. */
export async function stampStreamInheritVersionsAfter(
  projectId: string,
  streamId: string,
  at: Date,
): Promise<void> {
  const streamRef = doc(db, STREAMS_COLLECTION, streamId);
  const snap = await getDoc(streamRef);
  if (!snap.exists()) return;
  const data = snap.data()!;
  if (data.projectId !== projectId) throw new Error('This version history does not belong to the project');
  if (data.inheritVersionsAfter) return;
  await updateDoc(streamRef, { inheritVersionsAfter: Timestamp.fromDate(at) });
}

/**
 * Unversioned project files for the architect hub (Project folders).
 * `streamId` and `fileSection` null match docs where those fields are absent,
 * so version tracks and quotes stay out of the query.
 */
export function subscribeArchitectUnversionedProjectFiles(
  projectId: string,
  callback: (files: ProjectFile[]) => void,
  onError?: (err: Error) => void,
): () => void {
  // Missing fields and explicit null both match `== null`. A stored empty string does not,
  // so the second listener picks up legacy rows that wrote streamId: ''.
  const missingFields = query(
    collection(db, COLLECTION),
    where('projectId', '==', projectId),
    where('streamId', '==', null),
    where('fileSection', '==', null),
  );
  const blankStream = query(
    collection(db, COLLECTION),
    where('projectId', '==', projectId),
    where('streamId', '==', ''),
    where('fileSection', '==', null),
  );
  let missing: ProjectFile[] | null = null;
  let blank: ProjectFile[] | null = null;
  const emit = () => {
    if (missing == null && blank == null) return;
    const byId = new Map<string, ProjectFile>();
    for (const file of [...(missing ?? []), ...(blank ?? [])]) {
      if (file.id) byId.set(file.id, file);
    }
    callback(Array.from(byId.values()));
  };
  const unsubMissing = onSnapshot(
    missingFields,
    snap => {
      missing = snap.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }));
      emit();
    },
    err => {
      console.error('[projectFiles] architect unversioned subscribe', err);
      onError?.(err);
    },
  );
  const unsubBlank = onSnapshot(
    blankStream,
    snap => {
      blank = snap.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() }));
      emit();
    },
    err => {
      console.error('[projectFiles] architect blank-stream subscribe', err);
      blank = [];
      emit();
    },
  );
  return () => {
    unsubMissing();
    unsubBlank();
  };
}

export function subscribeProjectFiles(
  projectId: string,
  callback: (files: ProjectFile[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const q = query(
    collection(db, COLLECTION),
    where('projectId', '==', projectId),
    orderBy('uploadedAt', 'desc'),
  );
  return onSnapshot(
    q,
    snap => {
      callback(snap.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() })));
    },
    err => {
      console.error('[projectFiles] subscribe', err);
      onError?.(err);
    },
  );
}

const CATALOG_SNAPSHOT_LIMIT = 2500;

/** True for HTML viewer assets in the global catalog (by MIME or extension). */
export function isCatalogHtmlProjectFile(file: Pick<ProjectFile, 'mimeType' | 'name'>): boolean {
  const m = (file.mimeType || '').toLowerCase();
  if (m === 'text/html' || m === 'application/xhtml+xml') return true;
  const n = (file.name || '').toLowerCase();
  return n.endsWith('.html') || n.endsWith('.htm');
}

/**
 * Lightweight HTML detector for routing UI clicks (mirrors `isHtmlFile` but operates on
 * a stored `mimeType` + filename rather than a `File` object).
 */
export function isHtmlType(mimeType?: string, fileName?: string): boolean {
  return isCatalogHtmlProjectFile({ mimeType: mimeType || '', name: fileName || '' });
}

/** Recent project file rows (all projects) for internal HTML catalog views. */
export function subscribeRecentProjectFilesForCatalog(
  callback: (files: ProjectFile[]) => void,
  onError?: (error: Error) => void,
  maxDocs: number = CATALOG_SNAPSHOT_LIMIT,
): () => void {
  const q = query(collection(db, COLLECTION), orderBy('uploadedAt', 'desc'), limit(maxDocs));
  return onSnapshot(
    q,
    snap => {
      callback(snap.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() })));
    },
    err => {
      console.error('[subscribeRecentProjectFilesForCatalog]', err);
      onError?.(err as Error);
    },
  );
}

export async function updateProjectFileHtmlDescriptors(
  fileId: string,
  htmlModelDescriptors: string[],
  options?: { htmlPreviewImageUrl?: string },
): Promise<void> {
  const payload: Record<string, unknown> = { htmlModelDescriptors };
  if (options && 'htmlPreviewImageUrl' in options) {
    const t = (options.htmlPreviewImageUrl ?? '').trim();
    if (t) payload.htmlPreviewImageUrl = t;
    else payload.htmlPreviewImageUrl = deleteField();
  }
  await updateDoc(doc(db, COLLECTION, fileId), payload as DocumentData);
}

export async function deleteProjectFile(
  fileId: string,
  storagePath: string,
  reconcile?: {
    projectId: string;
    dealId?: string;
    /** Track the user is acting from; a version shown there but owned by another track (inherited pre-split history) is refused. */
    fromStreamId?: string;
  },
): Promise<void> {
  let streamId: string | undefined;
  let projectIdForStream: string | undefined;
  let fileUrl = '';
  let sourceLeadDocumentId: string | undefined;
  let effectiveStoragePath = storagePath;
  try {
    const snap = await getDoc(doc(db, COLLECTION, fileId));
    if (snap.exists()) {
      const d = snap.data();
      if (d.streamId && typeof d.streamId === 'string') {
        streamId = d.streamId;
        projectIdForStream = d.projectId as string;
      }
      fileUrl = (d.url as string) || '';
      sourceLeadDocumentId = (d.sourceLeadDocumentId as string) || undefined;
      if (!effectiveStoragePath && typeof d.storagePath === 'string') {
        effectiveStoragePath = d.storagePath;
      }
    }
  } catch {
    // ignore
  }
  if (reconcile?.fromStreamId && streamId && streamId !== reconcile.fromStreamId) {
    throw new Error('Pre-split versions are shared by every structure track and cannot be removed from here.');
  }

  try {
    await deleteHubFileReleasesForSourceFile(fileId);
  } catch (err) {
    console.warn('[deleteProjectFile] Hub release cleanup skipped:', err);
  }

  // Remove mirrored projectFiles rows created from the same lead upload (legacy rows lack sourceLeadDocumentId).
  if (fileUrl) {
    try {
      const mirrorSnap = await getDocs(
        query(collection(db, COLLECTION), where('url', '==', fileUrl)),
      );
      await Promise.all(
        mirrorSnap.docs.map(async (mirrorDoc) => {
          if (mirrorDoc.id === fileId) return;
          const mirror = mirrorDoc.data();
          const mirrorPath = (mirror.storagePath as string) || '';
          const isLeadMirror = !!mirror.sourceLeadDocumentId || !mirrorPath.trim();
          if (!isLeadMirror) return;
          await deleteDoc(mirrorDoc.ref);
        }),
      );
    } catch (err) {
      console.warn('[deleteProjectFile] Mirror cleanup skipped:', err);
    }
  }

  // Cascade to the originating lead document so synced files do not reappear.
  try {
    if (sourceLeadDocumentId) {
      const leadSnap = await getDoc(doc(db, 'leadDocuments', sourceLeadDocumentId));
      const leadStoragePath = leadSnap.exists()
        ? (leadSnap.data().storagePath as string | undefined)
        : undefined;
      await deleteLeadDocument(sourceLeadDocumentId, leadStoragePath);
    } else if (fileUrl) {
      const leadDoc = await findLeadDocumentByUrl(fileUrl);
      if (leadDoc?.id) {
        await deleteLeadDocument(leadDoc.id, leadDoc.storagePath);
      }
    }
  } catch (err) {
    console.warn('[deleteProjectFile] Lead document cleanup skipped:', err);
  }

  try {
    if (effectiveStoragePath) {
      await deleteObject(ref(storage, effectiveStoragePath));
    }
  } catch {
    // file may already be gone
  }
  await deleteDoc(doc(db, COLLECTION, fileId));

  if (streamId && projectIdForStream) {
    await reconcileStreamHeadAfterVersionDelete(projectIdForStream, streamId);
  }

  if (reconcile?.projectId) {
    await reconcileTimberListStateForProject(reconcile.projectId, reconcile.dealId);
    await reconcileHardwareListStateForProject(reconcile.projectId, reconcile.dealId);
  }
}

type SyncFileEntry = {
  name: string; url: string; mimeType: string; storagePath: string;
  uploadedBy: string; uploadedByName: string; size: number;
  sourceLeadDocumentId?: string;
};

export interface SyncPortalFilesOptions {
  /**
   * When true, *untagged* lead documents (no `dealId`) and comment attachments
   * are not pulled — only the client portal's deal-scoped uploads and lead
   * documents explicitly assigned to this `dealId`. Use when the lead already
   * has another project, to avoid dragging unassigned / sibling files into a
   * newly-created project.
   */
  skipLeadScopedFiles?: boolean;
}

/**
 * Pull files from the client portal, lead documents, and comment attachments
 * into a project's files collection.
 * Links to existing Storage URLs (no file duplication).
 * Skips files whose URLs already exist in the project to prevent duplicates.
 * Lead documents tagged with this `dealId` always sync; untagged docs and
 * comment attachments only sync when `skipLeadScopedFiles` is false.
 */
export async function syncPortalFilesToProject(
  projectId: string,
  dealId: string,
  leadId?: string,
  options?: SyncPortalFilesOptions,
): Promise<number> {
  try {
    const existingSnap = await getDocs(
      query(collection(db, COLLECTION), where('projectId', '==', projectId))
    );
    const existingUrls = new Set(existingSnap.docs.map(d => d.data().url as string));
    const filesToCreate: SyncFileEntry[] = [];

    const addFile = (f: Omit<SyncFileEntry, 'storagePath'>) => {
      if (existingUrls.has(f.url)) return;
      existingUrls.add(f.url);
      filesToCreate.push({ ...f, storagePath: '' });
    };

    // 1. Client portal: clientUploads + model/plans URLs
    const portalSnap = await getDocs(
      query(collection(db, 'clientPortals'), where('dealId', '==', dealId))
    );
    if (!portalSnap.empty) {
      const portal = portalSnap.docs[0].data();
      const clientUploads = (portal.clientUploads || []) as Array<{
        id?: string; name: string; url: string; type?: string;
        step?: string; dealId?: string;
      }>;
      const hasMulti = portal.projects && Object.keys(portal.projects).length > 1;

      for (const u of clientUploads) {
        if (hasMulti && u.dealId && u.dealId !== dealId) continue;
        addFile({ name: u.name || 'Client Upload', url: u.url, mimeType: u.type || 'application/octet-stream', size: 0, uploadedBy: 'client', uploadedByName: portal.clientName || 'Client' });
      }

      const projData = portal.projects?.[dealId];
      const modelUrl = projData?.modelUrl || portal.modelUrl;
      if (modelUrl) addFile({ name: '3D Model', url: modelUrl, mimeType: 'model/gltf-binary', size: 0, uploadedBy: 'portal', uploadedByName: 'Client Portal' });
      const modelImageUrl = projData?.modelImageUrl || portal.modelImageUrl;
      if (modelImageUrl) addFile({ name: 'Model Preview Image', url: modelImageUrl, mimeType: 'image/png', size: 0, uploadedBy: 'portal', uploadedByName: 'Client Portal' });
      const plansUrl = projData?.plansUrl || portal.plansUrl;
      if (plansUrl) addFile({ name: '2D Plans', url: plansUrl, mimeType: 'application/pdf', size: 0, uploadedBy: 'portal', uploadedByName: 'Client Portal' });
    }

    // 2 + 3. Lead documents + comment attachments.
    // Documents explicitly tagged with this dealId always sync. Untagged docs
    // and comment attachments only sync for the first project on a lead
    // (skipLeadScopedFiles === false).
    const resolvedLeadId = leadId || await resolveLeadIdFromDeal(dealId);
    if (resolvedLeadId) {
      const leadDocSnap = await getDocs(
        query(collection(db, 'leadDocuments'), where('leadId', '==', resolvedLeadId))
      );
      for (const ld of leadDocSnap.docs) {
        const d = ld.data();
        const docDealId = (d.dealId as string) || undefined;
        if (docDealId && docDealId !== dealId) continue;
        if (!docDealId && options?.skipLeadScopedFiles) continue;
        if (existingUrls.has(d.url as string)) continue;
        existingUrls.add(d.url as string);
        filesToCreate.push({
          name: d.name as string,
          url: d.url as string,
          mimeType: (d.mimeType as string) || 'application/octet-stream',
          storagePath: (d.storagePath as string) || '',
          size: (d.size as number) || 0,
          uploadedBy: (d.uploadedBy as string) || 'unknown',
          uploadedByName: (d.uploadedBy as string) || 'Lead Documents',
          sourceLeadDocumentId: ld.id,
        });
      }

      if (!options?.skipLeadScopedFiles) {
        const commentsSnap = await getDocs(
          query(leadCommentsCollectionRef(resolvedLeadId), orderBy('createdAt', 'asc'))
        );
        for (const cd of commentsSnap.docs) {
          const data = cd.data();
          const atts = (data.attachments || []) as Array<{ name: string; url: string; type: string; size: number }>;
          const authorName = (data.userName as string) || 'Comment';
          for (const att of atts) {
            addFile({
              name: att.name,
              url: att.url,
              mimeType: att.type || 'application/octet-stream',
              size: att.size || 0,
              uploadedBy: (data.userId as string) || 'comment',
              uploadedByName: authorName,
            });
          }
        }
      }
    }

    // 4. Write all new file records
    for (const f of filesToCreate) {
      await addDoc(collection(db, COLLECTION), {
        projectId,
        name: f.name,
        url: f.url,
        storagePath: f.storagePath,
        mimeType: f.mimeType,
        size: f.size,
        uploadedAt: Timestamp.now(),
        uploadedBy: f.uploadedBy,
        uploadedByName: f.uploadedByName,
        ...(f.sourceLeadDocumentId ? { sourceLeadDocumentId: f.sourceLeadDocumentId } : {}),
      });
    }

    if (filesToCreate.length > 0) {
      console.log(`[syncPortalFiles] Added ${filesToCreate.length} files to project ${projectId}`);
    }
    return filesToCreate.length;
  } catch (err) {
    console.error('[syncPortalFiles] Error syncing portal files:', err);
    return 0;
  }
}

/** Move all projectFiles + projectFileStreams rows from one timber project to another (precon → contracted merge). */
export async function reassignProjectFilesBetweenProjects(
  fromProjectId: string,
  toProjectId: string,
): Promise<number> {
  if (!fromProjectId || !toProjectId || fromProjectId === toProjectId) return 0;

  let moved = 0;
  const filesSnap = await getDocs(
    query(collection(db, COLLECTION), where('projectId', '==', fromProjectId)),
  );
  for (const d of filesSnap.docs) {
    await updateDoc(d.ref, { projectId: toProjectId });
    moved += 1;
  }

  const streamsSnap = await getDocs(
    query(collection(db, STREAMS_COLLECTION), where('projectId', '==', fromProjectId)),
  );
  for (const d of streamsSnap.docs) {
    await updateDoc(d.ref, { projectId: toProjectId });
  }

  if (moved > 0) {
    console.log(`[reassignProjectFiles] Moved ${moved} files from ${fromProjectId} → ${toProjectId}`);
  }
  return moved;
}

export type LinkCommentAttachmentOptions = {
  streamId?: string;
  versionLabel?: string;
  versionNote?: string;
};

/**
 * Link task-comment attachments into the project's files collection.
 * Deduplicates by URL so re-posting the same file is safe.
 * Optional version path: first attachment whose MIME matches the stream kind becomes a new version.
 */
export async function linkCommentAttachmentsToProject(
  projectId: string,
  attachments: Array<{ name: string; url: string; type: string; size: number }>,
  uploadedBy: string,
  uploadedByName: string,
  linkOptions?: LinkCommentAttachmentOptions,
): Promise<void> {
  if (!projectId || attachments.length === 0) return;
  try {
    const existingSnap = await getDocs(
      query(collection(db, COLLECTION), where('projectId', '==', projectId))
    );
    const existingUrls = new Set(existingSnap.docs.map(d => d.data().url as string));

    const streamId = linkOptions?.streamId?.trim();
    const versionLabel = linkOptions?.versionLabel?.trim();

    if (streamId && versionLabel) {
      const streamSnap = await getDoc(doc(db, STREAMS_COLLECTION, streamId));
      if (streamSnap.exists()) {
        const stream = streamFromFirestore({ id: streamSnap.id, data: () => streamSnap.data()! });
        if (stream.projectId === projectId) {
          const match = attachments.find(att =>
            fileMatchesStreamKind(att.type || '', att.name, stream.streamKind),
          );
          if (match && !existingUrls.has(match.url)) {
            await addLinkedVersionToStream(
              projectId,
              streamId,
              match,
              uploadedBy,
              uploadedByName,
              versionLabel,
              linkOptions?.versionNote,
            );
            existingUrls.add(match.url);
          }
        }
      }
    }

    for (const att of attachments) {
      if (existingUrls.has(att.url)) continue;
      existingUrls.add(att.url);
      await addDoc(collection(db, COLLECTION), {
        projectId,
        name: att.name,
        url: att.url,
        storagePath: '',
        mimeType: att.type || 'application/octet-stream',
        size: att.size || 0,
        uploadedAt: Timestamp.now(),
        uploadedBy,
        uploadedByName,
      });
    }
  } catch (err) {
    console.error('[linkCommentAttachments] Error linking files to project:', err);
  }
}

async function resolveLeadIdFromDeal(dealId: string): Promise<string | null> {
  try {
    const dealSnap = await getDoc(doc(db, 'enhancedDeals', dealId));
    if (dealSnap.exists()) return (dealSnap.data().leadId as string) || null;
    const legacyDeal = await getDoc(doc(db, 'deals', dealId));
    if (legacyDeal.exists()) return (legacyDeal.data().leadId as string) || null;
    return null;
  } catch {
    return null;
  }
}
